import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, DecimalPipe, JsonPipe, KeyValuePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
	AfterViewInit,
	Component,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FaceLandmarker, FaceLandmarkerResult, ImageEmbedder, NormalizedLandmark } from '@mediapipe/tasks-vision';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	IconComponent,
	INavHelp,
	InputComponent,
	LinkComponent,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	QrComponent,
	ScanFaceComponent,
	SpinnerComponent,
	SwitchComponent,
	TileComponent,
	TopnavComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { combineLatest, filter, interval, Subject, Subscription, takeUntil, timer } from 'rxjs';
import { findLastIndex } from 'src/app/core/utils/utils';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { IAlert } from 'src/app/core/models/alert.models';
import { LocalStorageKeys, operations } from 'src/app/core/models/auth.models';
import { ILoading } from 'src/app/core/models/loading.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { CookieService } from 'src/app/core/services/cookie.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ScreenshotService } from 'src/app/core/services/screenshot.service';
import { PolPreviewDiagnosticsBufferService } from 'src/app/core/services/pol-preview-diagnostics-buffer.service';
import { ModalChangeDeviceComponent } from 'src/app/shared/components/modal-change-device/modal-change-device.component';
import { ModalHelpNoCameraPermissionsComponent } from 'src/app/shared/components/modal-help-no-camera-permissions/modal-help-no-camera-permissions.component';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { VideoComponent } from 'src/app/shared/components/video/video.component';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { isStorageRetryError } from 'src/app/shared/utils/storage-retry.utils';
import { IMaster } from 'src/app/shared/models/master.models';
import { AnimationsService } from 'src/app/shared/services/animations.service';
import { AudioService } from 'src/app/shared/services/audio.service';
import { CameraService } from 'src/app/shared/services/camera.service';
import { CameraFocusService } from 'src/app/shared/services/camera-focus.service';
import { CameraPermissionsTimerService } from 'src/app/shared/services/camera-permissions-timer.service';
import { waitForVideoPreviewReady } from 'src/app/shared/utils/wait-for-video-preview-ready.util';
import { buildMediapipeGpuStallLogContext } from 'src/app/shared/utils/mediapipe-face-delegate.util';
import {
	onCameraPreviewSwitchCompleted,
	onCameraPreviewSwitchStarted,
} from 'src/app/shared/utils/camera-preview-switch.util';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { LottieThemeService } from 'src/app/shared/services/config/lottie/lottie-theme.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { FullscreenService } from 'src/app/shared/services/fullscreen.service';
import { IdentitySDKService, OperationType } from 'src/app/shared/services/identity-sdk.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { StreamingManagementService } from 'src/app/shared/services/streaming-management.service';
import { environment } from 'src/environments/environment';
import { TextToSpeechService } from '../../../shared/services/text-to-speech.service';
import {
	POL_FINISH_TIME,
	ILifeTest,
	ILifeTestDataReturn,
	IMiddleFace,
	IPolConf,
	IPolErrorResponse,
	IPolPayload,
	IPolResponse,
	IScanFaceAnimations,
	ISimilarity,
	LIFE_TEST_ID,
	POL_CAMERA_CHANGE_GRACE_MS,
	POL_DEGRADED_POLL_MS,
	POL_DETECT_MIN_INTERVAL_MS,
	POL_FAST_FRAME_THRESHOLD_MS,
	POL_FAST_STREAK_TO_DECREASE_N,
	POL_FRAME_RECOVERY_MAX_FAILURES_BEFORE_TECH_KO,
	POL_FRAME_RECOVERY_RETRY_COUNT,
	POL_FRAME_RECOVERY_RETRY_DELAY_MS,
	POL_LIFE_TEST_CYCLE_MAX_FRAMES,
	POL_LIFE_TEST_CYCLE_MIN_FRAMES,
	POL_MATCH_UI_STREAK_TO_CENTERED,
	POL_MATCH_UI_STREAK_TO_OFF_CENTER,
	POL_MIN_TIME_MS_PER_SECOND,
	POL_NO_FACE_MIN_CONSECUTIVE_FRAMES,
	POL_NO_FACE_RECENT_FACE_GRACE_MS,
	POL_NO_FACE_TIME,
	POL_ANALYSIS_DEAD_MIN_ELAPSED_MS,
	POL_OVAL_PREVIEW_DEAD_STREAK_THRESHOLD,
	POL_SLOW_FRAME_THRESHOLD_MS,
	POL_SLOW_STREAK_TO_INCREASE_N,
	POL_SUCCESS_FEEDBACK_FALLBACK_MS,
	POL_TYPE,
	IFRAME_RESULT,
	POL_WARNING_TIME,
	POL_BACK_TO_OCR_TIME,
	POL_MULTIPLE_FACE_MIN_BBOX_AREA_RATIO,
	POL_MULTIPLE_FACE_MIN_CENTER_SEPARATION_RATIO,
	POL_MULTIPLE_FACES_START_COOLDOWN_SEC,
	POL_UI_REFRESH_EVERY_N_FRAMES,
	IPolHistory,
	IPolPromptConfig,
	POL_SEG_HISTORY_INTERVAL,
} from '../../models/life-check';
import {
	PolLog,
	IFinishOperation,
	IOperation,
	IPageWarning,
	IPoint,
	IPolOperation,
	IPolSetting,
	TEST_STATUS,
	MODE,
	OPERATION_STATUS,
	OPERATION_TYPE,
	PAGE_WARNING,
	quality,
	TestStatus,
} from '../../models/user-identification';
import { LifeCheckService } from '../../services/pol/life-check.service';
import { PositionFaceService } from '../../services/pol/position-face.service';
import { ScoringFaceService } from '../../services/pol/scoring-face.service';
import { PolTimerManagerService } from '../../services/pol/timer-manager.service';
import { PolAudioHistoryService } from '../../services/pol/pol-audio-history.service';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ProcessService } from '../../services/process.service';
import { KoDebugImagesService } from '../../services/debug/ko-debug-images.service';
import {
	drawVideoCoverFrameToCanvas,
	mapCanvasPointToSensorNormalized,
	resolveVideoCoverSourceRect,
} from 'src/app/shared/services/video-cover-frame.util';
import { PolPreviewDevice } from '../../models/life-check';
import {
	approachFaceProtocol,
	getApproachFaceLabThresholds,
	getPolAnalysisExtraZoomFactor,
	getPolLivenessJpegCrop,
	getPolPayloadUseFullCanvas,
	getPolPayloadZoomFactor,
	lifeCheckConf,
} from './life-check-conf';
import { UserAgentService } from 'src/app/shared/services/user-agent.service';
import { HandsetLandscapeTileComponent } from 'src/app/shared/components/handset-landscape-tile/handset-landscape-tile.component';
import { OperationService } from 'src/app/shared/services/step.service';
import { FaceDepthRealLabService } from 'src/app/models/services/face-depth-real-lab.service';
import {
	DEFAULT_FACE_DEPTH_REAL_THRESHOLDS,
	FaceDepthRealScores,
	FaceDepthRoiSnapshot,
} from 'src/app/models/services/face-depth-real.models';
import { extractFaceRoiTextureMetrics } from 'src/app/models/services/face-3d-lab-geometry.util';

@Component({
	selector: 'app-life-check',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		DecimalPipe,
		KeyValuePipe,
		JsonPipe,
		AsyncPipe,
		NgClass,
		NgStyle,
		NgTemplateOutlet,
		LottieComponent,
		AlertComponent,
		ButtonComponent,
		ButtonIconComponent,
		IconComponent,
		InputComponent,
		LinkComponent,
		NavHelpComponent,
		ProgressComponent,
		PwcsLayoutComponent,
		QrComponent,
		ScanFaceComponent,
		SpinnerComponent,
		SwitchComponent,
		TileComponent,
		TopnavComponent,
		WatermarkComponent,
		VideoComponent,
		HandsetLandscapeTileComponent,
		ModalScanQrComponent,
		ModalHelpNoCameraPermissionsComponent,
		ModalChangeDeviceComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './life-check.component.html',
	styleUrls: ['./life-check.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class LifeCheckComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	operationService = inject(OperationService);

	@Input() operations: IOperation[];
	@Input() operation: IPolOperation;

	private applyOperationInput(value: IPolOperation): void {
		this.refreshTemplateCaches();
		if (value.config.iframe) {
			this.loadingService.showIframe(this.trans.instant('GENERAL.LOADING.CAMERA_INIT'));
		} else {
			this.activateLoading('GENERAL.LOADING.CAMERA_INIT');
		}
		this.lifeCheckConf = this.setPolSetting(value.config.setting);
		this.originalLifeCheckConf = JSON.parse(JSON.stringify(this.lifeCheckConf));
		this.allLifeTest = JSON.parse(JSON.stringify(this.lifeCheckConf.lifeTest));
		this.shuffleLifeTest();
		this.lifeCheckProgressValue = this.progressService.getValue(this.lifeCheckConf);
		if (this.operation?.config?.iframe) {
			localStorage.setItem(LocalStorageKeys.access_token, operations.old.token);
		} else {
			this.cookieService.set(LocalStorageKeys.access_token, operations.old.token, undefined, '/');
		}

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['operation']?.currentValue) {
			this.applyOperationInput(changes['operation'].currentValue as IPolOperation);
		}
	}

	// Textos y alertas
	public subtitle: string = '';

	/** Reserva el hueco del subtítulo en el layout cuando el texto real está vacío. */
	get layoutSubtitle(): string {
		return this.subtitle?.trim() ? this.subtitle : '\u00A0';
	}

	public alertContent: IAlert = { label: '', status: 'default' };
	public alertScanFaceText: string = '';
	public lifeCheckProgressValue = 0;
	public darkModeEnabled = false;
	public qrUrl = '';

	// Controles
	public isHuman: boolean = true;
	public formStats: FormControl = new FormControl(false);
	public formRect: FormControl = new FormControl(false);
	public controls: any;
	private countMultipleface: number = 0;
	private polLiveDebugFrame: string | null = null;
	private nosePath: Array<IPoint> = [];

	// Camara
	public video!: HTMLVideoElement;
	public canvas!: HTMLCanvasElement;
	public ctx!: CanvasRenderingContext2D;
	public stream: MediaStream;
	public allCameras: Array<MediaDeviceInfo> = [];
	public imageData16x9: string;
	public animationFrame: number;
	public forceVideoRemount = false;
	private lastVideoTime: number = -1;
	/** Último instante en que se ejecutó `detectForVideo` (capado para no superar ~30 FPS de inferencia). */
	private lastPolDetectCpuTimeMs: number = 0;
	private lifeTestCycleEveryNFrames = POL_LIFE_TEST_CYCLE_MIN_FRAMES;
	private lifeTestCycleFrameCounter = 0;
	/** Inicio de la ventana minTime de la prueba de vida activa (progreso UI, NIF-385). */
	private lifeTestValidationStartedAtMs: number | null = null;
	/** Inicio del minTime de middle-face (cuenta atrás UI, NIF-385 QA). */
	private middleFaceMinTimeStartedAtMs: number | null = null;
	/** Segundos restantes del minTime de centrado; `null` si no corre. */
	public middleFaceMinTimeCountdownSeconds: number | null = null;
	/** Cuenta atrás en óvalo durante holds centrados de approach-face (far/near). */
	public approachFaceHoldCountdownSeconds: number | null = null;
	/** Prueba con ventana minTime abierta (evita doble finalización timer + rAF, NIF-385). */
	private activeLifeTestMinTimeGate: LIFE_TEST_ID | null = null;
	/** Serializa `photosAreSimilar` entre timer diferido y rAF. */
	private polMinTimeFinalizeInFlight = false;
	/** Última prueba de gesto cuyo scan UI (check verde, medias máscaras) está montado. */
	private polUiLifeTestId: LIFE_TEST_ID | null = null;
	/** Prueba en fase de feedback onComplete gestionado por pol-audio-history. */
	private completingPolTestId: LIFE_TEST_ID | null = null;
	/** Intermedio entre pruebas: éxito breve + recentrado. */
	private interTestTransition: { phase: 'success' | 'recenter'; startedAtMs: number } | null = null;
	/** Mantiene el subtítulo del layout visible en móvil un instante tras salir del tutorial. */
	public polTutorialSubtitleHandoff = false;
	private polTutorialSubtitleHandoffTimeoutId: ReturnType<typeof setTimeout> | null = null;
	/** true en el rAF en que corrió `detectForVideo` (evita recalcular geometría con landmarks repetidos). */
	private polDetectRanThisFrame = false;
	private polSlowFrameStreak = 0;
	private polFastFrameStreak = 0;
	private lastVideoDimensions: { width: number; height: number } | null = null;
	private videoDimensionStableCount: number = 0;
	private cameraChangeTime: number = 0;

	private polOvalPreviewDeadStreak = 0;
	private polOvalPreviewDeadWarned = false;
	private polAnalysisDeadStreak = 0;
	private polAnalysisDeadWarned = false;
	private polPreviewWatchdogSub?: Subscription;
	private polPreviewWatchdogStarted = false;
	private polPreviewRecoveryInProgress = false;
	private polPreviewLastCurrentTime = -1;
	private polPreviewStallStreak = 0;
	private polPreviewBlackStreak = 0;
	private polPreviewLastRecoveryMs = 0;
	private polPreviewRecoveryCount = 0;
	private polPreviewSampleCanvas: HTMLCanvasElement | null = null;
	private polPreviewSampleCtx: CanvasRenderingContext2D | null = null;
	private polPreviewRemountInProgress = false;
	private polInvalidFrameCount = 0;
	private polInvalidFrameConsecutiveCount = 0;
	private polInvalidFrameRecoveryInProgress = false;
	private polInvalidFrameRecoveryFailures = 0;
	private polPreviewUnusableReported = false;
	private polRecoveryFailureLastLogMs = 0;
	private noFaceConsecutiveFrames = 0;
	private lastFaceDetectedAtMs = 0;
	private polPreviewDegradedPoll = false;
	private polDegradedPollTimeoutId: ReturnType<typeof setTimeout> | null = null;
	/** Switch manual/recovery en curso (`app-video`); evita escalar recovery POL en paralelo. */
	private polCameraSwitchInProgress = false;
	private polCameraStartCount = 0;
	private polVideoNodeSeq = 0;
	private polCurrentVideoNodeId: string | null = null;
	/** Lecturas consecutivas de `match` antes de cambiar el óvalo (histéresis UI). */
	private matchUiStreakReadings: boolean[] = [];

	/** Protocolo NIF-739 approach-face (profundidad estimada). */
	private approachFacePhase: 'idle' | 'far' | 'approach_cue' | 'near' | 'done' = 'idle';
	private approachFaceFarRoi: FaceDepthRoiSnapshot | null = null;
	private approachFaceNearRoi: FaceDepthRoiSnapshot | null = null;
	private approachFaceLiveRoi: FaceDepthRoiSnapshot | null = null;
	private approachFaceHoldAnchorMs = 0;
	private approachFaceCenteredAccumMs = 0;
	private approachFacePhaseStartedAtMs = 0;
	private approachFaceReadyAnchorMs = 0;
	/** Frames match/no-match durante el protocolo (PercentageTimeInTheMiddle). */
	private approachFaceMatchSamples: boolean[] = [];

	// Vision
	public faceLandmarker: FaceLandmarker;
	private runningMode: 'IMAGE' | 'VIDEO' = 'IMAGE';
	/** Promesa compartida para no repetir `setOptions(VIDEO)` en cada frame de arranque. */
	private polFaceLandmarkerVideoModeReady: Promise<void> | null = null;
	private results: FaceLandmarkerResult = undefined;

	// ImageEmbedder
	private imageEmbedder: ImageEmbedder;

	// Configuración de la prueba
	public lifeCheckConf: IPolConf;
	public originalLifeCheckConf: IPolConf;
	public allLifeTest: ILifeTest[];
	/** Centrado geométrico instantáneo (scoring y validación). */
	public match: boolean = false;
	/** Centrado suavizado para el óvalo de `tec-scan-face` (evita parpadeo discontinuo/continuo). */
	public matchUi: boolean = false;
	public done: boolean = false;
	public scanFaceAnimation: IScanFaceAnimations = {
		grow: false,
		check: false,
		halfRight: false,
		halfLeft: false,
		halfTop: false,
		halfBottom: false,
		smile: false,
		showArrow: false,
		positionArrow: null,
		progressValue: 0,
	};
	private subscriptionTimers: Subscription = new Subscription();

	// General
	private initOperation: boolean = false;
	public env = environment;
	public loading: ILoading = {
		show: false,
		text: '',
	};
	public isVisibleScan: boolean = false;
	public isHelpByNavHelp: boolean = false;
	public isVisibleHelpNoPermissionsCamera: boolean = false;
	public isVisibleChangeDevice: boolean = false;
	public permissionsCamera: boolean = true;
	/** Prueba de vida: timers de operación solo tras permisos de cámara y micrófono concedidos. */
	private cameraPermissionsReady = false;
	private cameraPermissionsTimerService = inject(CameraPermissionsTimerService);
	public navsHelp: Array<INavHelp> = this.navHelpService.getNavsHelp();
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getTutorialNavsHelp();
	private resultToReturn: any[] = [];
	private destroy$: Subject<void> = new Subject<void>();
	private languagePickerUi = inject(LanguagePickerUiService);
	private readonly userAgentService = inject(UserAgentService);
	private readonly faceDepthRealLab = inject(FaceDepthRealLabService);
	public pageWarning: IPageWarning = { show: false, type: null };
	public backToOcrPendingErrorCode: number | null = null;
	public PAGE_WARNING = PAGE_WARNING;
	public handsetLandscape: boolean = false;
	/** Calculado una vez en el constructor (UA); preview y análisis comparten valor. */
	public polPreviewZoomFactor: number;
	/** Dispositivo POL (UA); umbrales approach-face lab. */
	private polPreviewDevice: PolPreviewDevice;
	/** Multiplicador extra durante fase lejos de `approach-face` (protocolo lab). */
	private approachFaceZoomBoost = 1;
	/** Zoom del JPEG `/liveness` (PC zoom-out 0.7; móvil 1). */
	private polPayloadZoomFactor: number;
	/** PC: JPEG = canvas completo (sin tira 336/528). */
	private polPayloadUseFullCanvas: boolean;
	/** Preview PC más ancho que el óvalo (`POL_PC_PREVIEW_VIDEO_PX`). */
	public isPolPreviewPc: boolean;
	public openQR: boolean = false;
	public tutorialLottieOptions: AnimationOptions | null = null;
	private sendDataComplete: boolean = false;
	public IFRAME_RESULT: any = IFRAME_RESULT;
	private iframeSwitchCamera: boolean = false;
	private tutorialLottieSub: Subscription | null = null;
	private lastPolCameraErrorLogAt = 0;
	private secondlyTimerSubscription: Subscription | null = null;

	// Iframe
	public parentMsg: any;
	public dataToParent: FormControl = new FormControl('Texto para enviar al padre');
	public testMode: boolean = false;
	private browserstack: boolean = false;

	constructor(
		public lifeCheckService: LifeCheckService,
		public progressService: ProgressService,
		public processHelperService: ProcessHelperService,
		public tts: TextToSpeechService,
		public audioService: AudioService,
		public cloudLogsService: CloudLogsService,
		public positionFaceService: PositionFaceService,
		public languageService: LanguageService,
		public designService: DesignService,
		public auth: AuthService,
		private processService: ProcessService,
		private breakpointObserver: BreakpointObserver,
		private route: ActivatedRoute,
		private animationsService: AnimationsService,
		private scoringFaceService: ScoringFaceService,
		private fullscreenService: FullscreenService,
		private cookieService: CookieService,
		private trans: TranslateService,
		private loadingService: LoadingService,
		private cameraService: CameraService,
		private cameraFocusService: CameraFocusService,
		private screenShotService: ScreenshotService,
		private lottieThemeService: LottieThemeService,
		private navHelpService: NavHelpService,
		private streamingManagementService: StreamingManagementService,
		private errorService: ErrorService,
		public timerManagerService: PolTimerManagerService,
		private masterService: MasterService,
		private identitySDKService: IdentitySDKService,
		private koDebugImagesService: KoDebugImagesService,
		private polPreviewDiagnosticsBuffer: PolPreviewDiagnosticsBufferService,
		private polAudioHistoryService: PolAudioHistoryService,
	) {
		const polDevice: PolPreviewDevice = this.userAgentService.isMobile() ? 'mobile' : 'pc';
		this.polPreviewDevice = polDevice;
		this.isPolPreviewPc = polDevice === 'pc';
		this.polPreviewZoomFactor = getPolAnalysisExtraZoomFactor(polDevice);
		this.polPayloadZoomFactor = getPolPayloadZoomFactor(polDevice);
		this.polPayloadUseFullCanvas = getPolPayloadUseFullCanvas(polDevice);

		this.route.queryParams.subscribe((params: any) => {
			this.testMode = Boolean(params.testmode);
			this.browserstack = Boolean(params.browserstack);
		});

		this.breakpointObserver
			.observe([Breakpoints.HandsetLandscape])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.handsetLandscape = result.matches;
				this.updateSubtitle();
				if (this.handsetLandscape) {
					this.stopTotalTime();
					if (!this.pageWarning.show) {
						this.startWarningTime();
					}
				} else {
					this.startTotalTime();
					if (!this.pageWarning.show) {
						this.stopWarningTime();
					}
				}
			});

		this.activateLoading('GENERAL.LOADING.LIFE_CHECK_INITIAL');

		window.onmessage = (e) => {
			if (!environment.production) {
				console.log(e);
			}
			if (this.operation?.config?.iframe && e?.data && e.data.code === 'life-test') {
				if (e.data.tkn) {
					localStorage.setItem(LocalStorageKeys.access_token, e.data.tkn);
				}
				if (e.data.qr) {
					this.auth.setQRUrl(e.data.qr);
					this.qrUrl = this.auth.getQRUrl();
				}
				if (e.data.locale) {
					this.languageService.setLanguage(e.data.locale);
				}
				if (e.data.operation_id) {
					this.lifeCheckConf.general.operationId = e.data.operation_id;
				}
				if (e.data.switch_cameras) {
					this.iframeSwitchCamera = true;
				}
			} else {
				// Solo loguear mensajes relacionados con la comunicación del iframe para evitar logs innecesarios
				if (this.operation?.config?.iframe) {
					this.cloudLogsService.sendCloudLogs(
						{ operationId: 'Error iframe comunication', observation: e?.toString() },
						PolLog.PRELOAD,
						LogType.Error,
					);
				}
			}
		};
	}

	ngOnInit(): void {
		this.positionFaceService.setDraw(false);
		this.refreshTemplateCaches();
		this.refreshTutorialLottieOptions();
		this.designService
			.getConfigDesign()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.refreshTemplateCaches();
				this.refreshTutorialLottieOptions();
			});

		this.cameraService.allCameras$.pipe(takeUntil(this.destroy$)).subscribe((cameras: MediaDeviceInfo[]) => {
			this.allCameras = cameras;
			if (this.allCameras.length && this.lifeCheckConf?.general?.observations) {
				this.lifeCheckConf.general.observations.push(JSON.stringify(this.allCameras));
			}
			if (this.controls) {
				this.controls = {
					...this.controls,
					mediaDevices: this.allCameras,
				};
			} else {
				this.controls = {
					mediaDevices: this.allCameras,
				};
			}
			const foundIndex = this.navsHelp.findIndex((nav: INavHelp) => nav.id === NavHelpAction.ROTATE);
			if (this.allCameras && this.allCameras.length > 1) {
				if (foundIndex === -1) {
					this.navsHelp = this.navHelpService.getNavsHelpWithCamera();
				}
			} else {
				if (foundIndex > -1) {
					this.navsHelp.splice(foundIndex, 1);
				}
			}
		});
	}

	ngAfterViewInit(): void {
		// Asegurar que las cámaras se carguen inmediatamente para que estén disponibles cuando se envíe el resultado
		this.cameraService.getAllCameras();

		// Iniciar operación
		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
		// Sin tutorial
		if (!this.operation.config.tutorial) {
			this.audioService.preloadLifeCheckAudios();
		}
		this.checkCameraPermissions();

		// Load models
		combineLatest([this.processHelperService.getFaceModel(), this.processHelperService.getImageEmbedderModel()])
			.pipe(filter(([faceModel, imageEmbedderModel]) => !!faceModel && !!imageEmbedderModel))
			.subscribe(([faceModel, imageEmbedderModel]: [FaceLandmarker, ImageEmbedder]) => {
				if (faceModel) {
					this.faceLandmarker = faceModel;
				}
				if (imageEmbedderModel) {
					this.imageEmbedder = imageEmbedderModel;
				}
				if (this.imageEmbedder && this.faceLandmarker) {
					void this.ensureFaceLandmarkerVideoMode();
					this.polPreviewDiagnosticsBuffer.startSession();
					// Si el video ya está listo (loadeddata ya se disparó), iniciar predictWebcam manualmente
					if (this.video && this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
						this.predictWebcam();
					}
					if (window.location.hostname !== 'localhost' && environment.saveScreenShot) {
						this.screenShotService.startSaveScreenshot(PolLog.SCREENSHOT);
					}
					this.startPreviewWatchdog();
				}
			});
	}

	/**
	 * Watchdog de preview: detecta estados "playing" pero visualmente anómalos y lanza auto-recuperación.
	 */
	private startPreviewWatchdog(): void {
		if (this.polPreviewWatchdogStarted) return;
		this.polPreviewWatchdogStarted = true;
		this.polPreviewWatchdogSub = interval(1500)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.runPreviewWatchdog());
	}

	private runPreviewWatchdog(): void {
		if (this.isWithinCameraChangeGrace()) {
			this.polPreviewStallStreak = 0;
			this.polPreviewBlackStreak = 0;
			this.polPreviewLastCurrentTime = -1;
			this.polOvalPreviewDeadStreak = 0;
			this.polAnalysisDeadStreak = 0;
			return;
		}
		this.tickOvalPreviewDeadStreak();

		const middleFace = this.lifeCheckConf?.middleFace;
		const inMiddleFace =
			!!middleFace?.startAt &&
			!middleFace.complete &&
			middleFace.status !== TestStatus.OnComplete &&
			this.isPolLivenessProcessingFrame() &&
			!this.polAnalysisDeadWarned &&
			!this.isWithinCameraChangeGrace();

		if (inMiddleFace) {
			const elapsedMs = Date.now() - new Date(middleFace.startAt).getTime();
			const scanRect = this.getScanFaceRect();
			const lmCount = this.results?.faceLandmarks?.length ?? 0;
			const blendCount = this.results?.faceBlendshapes?.length ?? 0;
			const previewAlive =
				this.shouldRunPreviewWatchdog() &&
				!this.isPolStreamTrackDead() &&
				!!scanRect &&
				scanRect.width > 0 &&
				scanRect.height > 0 &&
				(this.canvas?.width ?? 0) > 0;

			const analysisDead =
				elapsedMs >= POL_ANALYSIS_DEAD_MIN_ELAPSED_MS && previewAlive && lmCount === 0 && blendCount === 0;

			if (analysisDead) {
				this.polAnalysisDeadStreak++;
				if (this.polAnalysisDeadStreak >= POL_OVAL_PREVIEW_DEAD_STREAK_THRESHOLD) {
					this.polAnalysisDeadWarned = true;
					const mediapipeGpu = buildMediapipeGpuStallLogContext();
					const videoTrack = this.stream?.getVideoTracks?.()?.[0];
					this.cloudLogsService.sendCloudLogs(
						{
							polAnalysisDeadWhilePreviewAlive: {
								streak: this.polAnalysisDeadStreak,
								elapsedMs,
								scanFaceRect: {
									width: Math.round(scanRect!.width),
									height: Math.round(scanRect!.height),
								},
								video: {
									width: this.video?.videoWidth ?? 0,
									height: this.video?.videoHeight ?? 0,
									currentTime: Math.round((this.video?.currentTime ?? 0) * 1000) / 1000,
									paused: this.video?.paused ?? null,
									readyState: this.video?.readyState ?? null,
									ended: this.video?.ended ?? null,
								},
								canvas: {
									width: this.canvas?.width ?? 0,
									height: this.canvas?.height ?? 0,
								},
								previewVideoLikelyBlack: this.video ? this.isVideoFrameLikelyBlack(this.video) : null,
								analysisCanvasLikelyBlack: this.canvas?.width
									? this.isVideoFrameLikelyBlack(this.canvas)
									: null,
								stallStreak: this.polPreviewStallStreak,
								blackStreak: this.polPreviewBlackStreak,
								landmarkCount: lmCount,
								blendshapeCount: blendCount,
								hasResultsObject: this.results != null,
								...this.buildPolAttemptDiagContext(),
								streamTrack: {
									readyState: videoTrack?.readyState ?? null,
									muted: videoTrack?.muted ?? null,
									enabled: videoTrack?.enabled ?? null,
								},
								mediapipeGpu,
							},
						},
						PolLog.IN_PROCESS,
						LogType.Warning,
					);
				}
			} else {
				this.polAnalysisDeadStreak = 0;
			}
		} else {
			this.polAnalysisDeadStreak = 0;
		}

		if (!this.shouldRunPreviewWatchdog() || !this.video) {
			this.polPreviewStallStreak = 0;
			this.polPreviewBlackStreak = 0;
			this.polPreviewLastCurrentTime = -1;
			return;
		}

		const currentTime = this.video.currentTime;
		if (this.polPreviewLastCurrentTime >= 0 && Math.abs(currentTime - this.polPreviewLastCurrentTime) < 0.001) {
			this.polPreviewStallStreak++;
		} else {
			this.polPreviewStallStreak = 0;
		}
		this.polPreviewLastCurrentTime = currentTime;

		const isBlackLike = this.isVideoFrameLikelyBlack(this.video);
		this.polPreviewBlackStreak = isBlackLike ? this.polPreviewBlackStreak + 1 : 0;

		const shouldRecover = this.polPreviewStallStreak >= 3 || this.polPreviewBlackStreak >= 3;
		const cooldownActive = Date.now() - this.polPreviewLastRecoveryMs < 8000;
		if (!shouldRecover || cooldownActive || this.polPreviewRecoveryInProgress) return;

		const reason = this.polPreviewStallStreak >= 3 ? 'stalledCurrentTime' : 'blackFrameStreak';
		this.polPreviewDiagnosticsBuffer.recordWatchdogTrigger();
		void this.recoverPreview(reason);
	}

	private shouldRunPreviewWatchdog(): boolean {
		if (!this.permissionsCamera || !this.cameraPermissionsReady) return false;
		if (!this.video || !this.stream) return false;
		if (this.loading.show || this.operation?.config?.tutorial || this.pageWarning.show || this.handsetLandscape)
			return false;
		return (
			this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
			this.video.videoWidth > 0 &&
			this.video.videoHeight > 0 &&
			!!this.stream.active
		);
	}

	private isVideoFrameLikelyBlack(source: HTMLVideoElement | HTMLCanvasElement): boolean {
		try {
			if (!this.polPreviewSampleCanvas) {
				this.polPreviewSampleCanvas = document.createElement('canvas');
				this.polPreviewSampleCanvas.width = 24;
				this.polPreviewSampleCanvas.height = 24;
				this.polPreviewSampleCtx = this.polPreviewSampleCanvas.getContext('2d', { willReadFrequently: true });
			}
			if (!this.polPreviewSampleCtx || !this.polPreviewSampleCanvas) return false;

			this.polPreviewSampleCtx.drawImage(
				source,
				0,
				0,
				this.polPreviewSampleCanvas.width,
				this.polPreviewSampleCanvas.height,
			);
			const data = this.polPreviewSampleCtx.getImageData(
				0,
				0,
				this.polPreviewSampleCanvas.width,
				this.polPreviewSampleCanvas.height,
			).data;

			let sum = 0;
			let sumSq = 0;
			let pixels = 0;
			for (let i = 0; i < data.length; i += 4) {
				const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
				sum += lum;
				sumSq += lum * lum;
				pixels++;
			}
			if (!pixels) return false;
			const mean = sum / pixels;
			const variance = Math.max(0, sumSq / pixels - mean * mean);
			const stdDev = Math.sqrt(variance);
			return mean < 8 && stdDev < 6;
		} catch {
			return false;
		}
	}

	private isWithinCameraChangeGrace(): boolean {
		return this.cameraChangeTime > 0 && Date.now() - this.cameraChangeTime < POL_CAMERA_CHANGE_GRACE_MS;
	}

	/** No remount ni `changeCamera` automático mientras el usuario cambia cámara o en gracia post-switch. */
	private shouldDeferPolRecoveryEscalation(): boolean {
		return this.isWithinCameraChangeGrace() || this.polCameraSwitchInProgress;
	}

	/** Tras switch manual o `cameraChanged`: evita KO técnico y resets de recovery durante la estabilización. */
	private markCameraChangeGrace(): void {
		this.cameraChangeTime = Date.now();
		this.stream = this.cameraService.stream ?? this.stream;
		this.polInvalidFrameRecoveryFailures = 0;
		this.polInvalidFrameConsecutiveCount = 0;
		this.polPreviewStallStreak = 0;
		this.polPreviewBlackStreak = 0;
		this.polOvalPreviewDeadStreak = 0;
		this.polAnalysisDeadStreak = 0;
		this.polAnalysisDeadWarned = false;
		this.videoDimensionStableCount = 0;
		this.lastVideoDimensions = null;
	}

	public onCameraChanged(): void {
		this.markCameraChangeGrace();
		this.polPreviewDiagnosticsBuffer.recordCameraSwitch();
	}

	private async recoverPreview(reason: string): Promise<boolean> {
		if (this.shouldDeferPolRecoveryEscalation()) {
			return false;
		}
		const now = Date.now();
		const cooldownRemainingMs = 5000 - (now - this.polPreviewLastRecoveryMs);
		if (this.polPreviewRecoveryInProgress || !this.video || cooldownRemainingMs > 0) {
			return false;
		}
		this.polPreviewRecoveryInProgress = true;
		this.polPreviewLastRecoveryMs = Date.now();
		this.polPreviewRecoveryCount++;

		let recovered = false;
		try {
			const video = this.video;
			const activeStream = this.stream?.active ? this.stream : this.cameraService.stream;
			let strategy: 'softRebind' | 'componentRemount' | 'changeCamera' = 'softRebind';
			this.cloudLogsService.sendCloudLogs(
				{
					polPreviewWatchdog: {
						action: 'recoverStart',
						reason,
						recoveryCount: this.polPreviewRecoveryCount,
						state: this.getPreviewDiagnosticsSnapshot(video),
						...this.buildPolAttemptDiagContext(),
					},
				},
				PolLog.PRELOAD,
				LogType.Warning,
			);

			video.pause();
			video.srcObject = null;
			await this.sleep(80);

			if (activeStream?.active) {
				video.srcObject = activeStream;
				await video.play().catch(() => undefined);
			}
			await this.sleep(220);

			recovered = this.isPreviewHealthy(video);

			// Durante gracia o switch en curso: no remontar ni rotar cámara (evita carrera con el usuario).
			if (!recovered && !this.shouldDeferPolRecoveryEscalation()) {
				strategy = 'componentRemount';
				recovered = await this.remountVideoComponentAndCheck();
			}

			if (!recovered) {
				this.cloudLogsService.sendCloudLogs(
					{
						polPreviewWatchdog: {
							action: 'recover',
							reason,
							recoveryCount: this.polPreviewRecoveryCount,
							strategy,
							recovered,
							...this.buildPolAttemptDiagContext(),
						},
					},
					PolLog.PRELOAD,
					LogType.Warning,
				);
			}
		} finally {
			this.polPreviewRecoveryInProgress = false;
			this.polPreviewStallStreak = 0;
			this.polPreviewBlackStreak = 0;
			this.polPreviewDiagnosticsBuffer.recordRecoveryAttempt(recovered);
		}
		return recovered;
	}

	private isPreviewHealthy(video: HTMLVideoElement | null = this.video): boolean {
		if (!video) return false;
		return (
			video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
			video.videoWidth > 0 &&
			video.videoHeight > 0 &&
			!this.isVideoFrameLikelyBlack(video)
		);
	}

	private async remountVideoComponentAndCheck(): Promise<boolean> {
		if (this.polPreviewRemountInProgress) return false;
		this.polPreviewRemountInProgress = true;
		try {
			if (this.animationFrame) {
				window.cancelAnimationFrame(this.animationFrame);
				this.animationFrame = 0;
			}
			this.cancelDegradedPoll();

			this.forceVideoRemount = true;
			await this.sleep(180);
			this.forceVideoRemount = false;
			await this.sleep(700);

			return this.isPreviewHealthy();
		} finally {
			this.polPreviewRemountInProgress = false;
		}
	}

	private getPreviewDiagnosticsSnapshot(video: HTMLVideoElement | null): Record<string, unknown> {
		const v = video ?? this.video;
		const s = this.stream ?? this.cameraService.stream;
		const track = s?.getVideoTracks?.()?.[0];
		return {
			video: v
				? {
						readyState: v.readyState,
						paused: v.paused,
						currentTime: Math.round(v.currentTime * 1000) / 1000,
						videoWidth: v.videoWidth,
						videoHeight: v.videoHeight,
					}
				: null,
			stream: s ? { active: s.active, id: s.id ?? null } : null,
			videoTrack: track ? { readyState: track.readyState, muted: track.muted } : null,
			watchdog: {
				stallStreak: this.polPreviewStallStreak,
				blackStreak: this.polPreviewBlackStreak,
			},
		};
	}

	private sleep(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	private getPreviewRect(video: HTMLVideoElement | null = this.video): DOMRect | null {
		if (!video) return null;
		try {
			return video.getBoundingClientRect();
		} catch {
			return null;
		}
	}

	private isCurrentFrameUsable(): boolean {
		if (!this.video) return false;
		if (this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return false;
		if (this.video.videoWidth <= 0 || this.video.videoHeight <= 0) return false;
		const rect = this.getPreviewRect(this.video);
		if (!rect || rect.width <= 0 || rect.height <= 0) return false;
		return true;
	}

	/** Stream con frame válido para inferencia; no exige layout CSS del `<video>` medido. */
	private canRunPolDetectionOnCurrentFrame(): boolean {
		if (!this.video) return false;
		if (this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return false;
		return this.video.videoWidth > 0 && this.video.videoHeight > 0;
	}

	/**
	 * MediaPipe se crea en modo IMAGE; pasar a VIDEO antes del primer `detectForVideo` evita
	 * bloquear el hilo principal en el arranque de la prueba (cuando ya hay cámara visible).
	 */
	private ensureFaceLandmarkerVideoMode(): Promise<void> {
		if (this.runningMode === 'VIDEO' || !this.faceLandmarker) {
			return Promise.resolve();
		}
		if (!this.polFaceLandmarkerVideoModeReady) {
			this.polFaceLandmarkerVideoModeReady = this.faceLandmarker
				.setOptions({ runningMode: 'VIDEO' })
				.then(() => {
					this.runningMode = 'VIDEO';
				})
				.catch((error: unknown) => {
					this.polFaceLandmarkerVideoModeReady = null;
					throw error;
				});
		}
		return this.polFaceLandmarkerVideoModeReady;
	}

	private getScanFaceRect(): DOMRect | null {
		const root =
			(this.video?.closest('tec-scan-face') as HTMLElement | null) ??
			(document.querySelector('tec-scan-face') as HTMLElement | null);
		if (!root) return null;
		try {
			return root.getBoundingClientRect();
		} catch {
			return null;
		}
	}

	private isPolStreamTrackDead(): boolean {
		const s = this.stream;
		if (!s?.active) return true;
		const track = s.getVideoTracks?.()[0];
		return !track || track.readyState === 'ended';
	}

	private isOvalPreviewDead(): boolean {
		if (!this.video) return false;
		const videoDead = this.video.videoWidth <= 0 || this.video.videoHeight <= 0;
		if (!videoDead) return false;
		const rect = this.getScanFaceRect();
		return !rect || rect.width <= 0 || rect.height <= 0;
	}

	private isPolPreviewDeadOrStreamBroken(): boolean {
		return this.isPolStreamTrackDead() || this.isOvalPreviewDead();
	}

	private shouldEvaluateOvalPreviewDead(): boolean {
		if (this.polOvalPreviewDeadWarned) return false;
		if (!this.permissionsCamera || !this.cameraPermissionsReady) return false;
		if (!this.video) return false;
		if (this.loading.show || this.operation?.config?.tutorial || this.handsetLandscape) return false;
		if (this.pageWarning.show) return false;
		if (this.isWithinCameraChangeGrace()) return false;
		return true;
	}

	private tickOvalPreviewDeadStreak(): void {
		if (!this.shouldEvaluateOvalPreviewDead()) {
			this.polOvalPreviewDeadStreak = 0;
			return;
		}
		if (!this.isPolPreviewDeadOrStreamBroken()) {
			this.polOvalPreviewDeadStreak = 0;
			return;
		}
		this.polOvalPreviewDeadStreak++;
		if (this.polOvalPreviewDeadStreak < POL_OVAL_PREVIEW_DEAD_STREAK_THRESHOLD) return;

		this.handleOvalPreviewDeadAsPermissionsDenied();
	}

	/** Preview muerto persistente: mismo flujo UX que permisos denegados/revocados (no `CAMERA_ERROR`). */
	private handleOvalPreviewDeadAsPermissionsDenied(): void {
		this.polOvalPreviewDeadWarned = true;
		const rect = this.getScanFaceRect();
		this.cloudLogsService.sendCloudLogs(
			{
				ovalPreviewDeadPermissionsFlow: {
					streak: this.polOvalPreviewDeadStreak,
					videoWidth: this.video!.videoWidth,
					videoHeight: this.video!.videoHeight,
					scanFaceRect: rect ? { width: Math.round(rect.width), height: Math.round(rect.height) } : null,
				},
			},
			PolLog.IN_PROCESS,
			LogType.Error,
		);
		this.stopPolCaptureLoop();
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
		this.hidePreviewLoading();
		if (!this.browserstack) {
			this.permissionsCamera = false;
			this.updateSubtitle();
		}
		this.onPermissionsDenied();
	}

	private buildFrameUsabilitySnapshot(context: string): Record<string, unknown> {
		const rect = this.getPreviewRect(this.video);
		return {
			context,
			frameUsable: this.isCurrentFrameUsable(),
			video: this.video
				? {
						readyState: this.video.readyState,
						videoWidth: this.video.videoWidth,
						videoHeight: this.video.videoHeight,
						currentTime: Math.round(this.video.currentTime * 1000) / 1000,
					}
				: null,
			previewRect: rect
				? {
						x: Math.round(rect.x),
						y: Math.round(rect.y),
						width: Math.round(rect.width),
						height: Math.round(rect.height),
					}
				: null,
			canvas: this.canvas
				? {
						width: this.canvas.width,
						height: this.canvas.height,
					}
				: null,
			generalCountTries: this.lifeCheckConf?.general?.countTries ?? null,
			cameraStartCount: this.polCameraStartCount,
			videoNodeId: this.polCurrentVideoNodeId,
			invalidFrameCount: this.polInvalidFrameCount,
			invalidFrameConsecutiveCount: this.polInvalidFrameConsecutiveCount,
		};
	}

	/**
	 * NIF-1091: contexto de intento/fase PoL para correlacionar analysis-dead, retries y recover de preview.
	 */
	private buildPolAttemptDiagContext(): Record<string, unknown> {
		const general = this.lifeCheckConf?.general;
		const middleFace = this.lifeCheckConf?.middleFace;
		const pending = this.getCurrentPendingLifeTest();
		const countTries = general?.countTries ?? null;
		const videoTrack = this.stream?.getVideoTracks?.()?.[0];
		return {
			generalStatus: general?.status ?? null,
			generalCountTries: countTries,
			generalTriesMax: general?.tries ?? null,
			isRetryAttempt: typeof countTries === 'number' ? countTries > 1 : null,
			middleFaceComplete: middleFace?.complete ?? null,
			middleFaceStatus: middleFace?.status ?? null,
			middleFaceHasStartAt: !!middleFace?.startAt,
			pendingLifeTestId: pending?.lifeTestId ?? null,
			pendingLifeTestStatus: pending?.status ?? null,
			pageWarningShow: this.pageWarning?.show ?? null,
			pageWarningType: this.pageWarning?.show ? this.pageWarning.type : null,
			loadingShow: this.loading?.show ?? null,
			tutorialActive: !!this.operation?.config?.tutorial,
			faceLandmarkerPresent: !!this.faceLandmarker,
			runningMode: this.runningMode,
			hasResultsObject: this.results != null,
			landmarkCount: this.results?.faceLandmarks?.length ?? 0,
			blendshapeCount: this.results?.faceBlendshapes?.length ?? 0,
			msSinceLastFace: this.lastFaceDetectedAtMs ? Date.now() - this.lastFaceDetectedAtMs : null,
			noFaceConsecutiveFrames: this.noFaceConsecutiveFrames,
			previewRecoveryCount: this.polPreviewRecoveryCount,
			cameraStartCount: this.polCameraStartCount,
			analysisDeadWarned: this.polAnalysisDeadWarned,
			streamActive: this.stream?.active ?? null,
			videoTrackReadyState: videoTrack?.readyState ?? null,
			videoPaused: this.video?.paused ?? null,
			videoReadyState: this.video?.readyState ?? null,
		};
	}

	private async waitForUsableFrame(reason: string): Promise<boolean> {
		for (let attempt = 1; attempt <= POL_FRAME_RECOVERY_RETRY_COUNT; attempt++) {
			if (this.isCurrentFrameUsable()) return true;
			await this.recoverPreview(`frameUnusable:${reason}:attempt-${attempt}`);
			if (this.isCurrentFrameUsable()) return true;
			if (attempt < POL_FRAME_RECOVERY_RETRY_COUNT) {
				await this.sleep(POL_FRAME_RECOVERY_RETRY_DELAY_MS);
			}
		}
		return this.isCurrentFrameUsable();
	}

	private async handleUnusableFrame(reason: string): Promise<boolean> {
		if (this.isWithinCameraChangeGrace()) {
			return false;
		}

		this.polInvalidFrameCount++;
		this.polInvalidFrameConsecutiveCount++;
		this.stopNoFaceTime();
		this.noFaceConsecutiveFrames = 0;
		if (reason !== 'videoZeroDimensions') {
			this.polPreviewDiagnosticsBuffer.recordInvalidFrame(reason);
		}
		this.syncPreviewBufferSnapshot(reason);
		this.maybeFlushPolPreviewDiagnosticsBuffer('invalidFrame');

		if (!environment.polFrameRecoveryEnabled || this.polInvalidFrameRecoveryInProgress) {
			return false;
		}

		this.polInvalidFrameRecoveryInProgress = true;
		try {
			const recovered = await this.waitForUsableFrame(reason);
			if (recovered) {
				this.polInvalidFrameConsecutiveCount = 0;
				this.polInvalidFrameRecoveryFailures = 0;
				this.polPreviewDiagnosticsBuffer.markPreviewRecovered();
				this.polPreviewDiagnosticsBuffer.flushFrameCounters();
				return true;
			}

			this.polInvalidFrameRecoveryFailures++;
			if (Date.now() - this.polRecoveryFailureLastLogMs > 5000) {
				this.polRecoveryFailureLastLogMs = Date.now();
				if (this.availableToLogRecoveryFailure()) {
					this.cloudLogsService.sendCloudLogs(
						{
							previewRecoveryFailed: {
								reason,
								recoveryFailures: this.polInvalidFrameRecoveryFailures,
								...this.buildFrameUsabilitySnapshot(reason),
								...this.polPreviewDiagnosticsBuffer.buildSummary({
									flushReason: 'previewRecoveryFailed',
								}),
							},
						},
						PolLog.IN_PROCESS,
						LogType.Warning,
					);
				}
				this.polPreviewDiagnosticsBuffer.flushFrameCounters();
			}

			if (
				environment.polPreviewUnusableTechnicalKoEnabled &&
				this.isPolLivenessProcessingFrame() &&
				!this.polPreviewUnusableReported &&
				!this.isWithinCameraChangeGrace() &&
				this.polInvalidFrameRecoveryFailures >= POL_FRAME_RECOVERY_MAX_FAILURES_BEFORE_TECH_KO
			) {
				this.polPreviewUnusableReported = true;
				if (this.availableToLogRecoveryFailure()) {
					this.cloudLogsService.sendCloudLogs(
						{
							previewUnusableOutcome: {
								mode: 'finalKo',
								reason,
								recoveryFailures: this.polInvalidFrameRecoveryFailures,
								...this.buildFrameUsabilitySnapshot(reason),
								...this.polPreviewDiagnosticsBuffer.buildSummary({
									flushReason: 'previewUnusableOutcome',
								}),
							},
						},
						PolLog.IN_PROCESS,
						LogType.Error,
					);
				}
				this.lifeCheckConf.general.status = TestStatus.OnFail;
				this.sendResult(false, TEST_STATUS.KO_PREVIEW_UNUSABLE);
				this.stopTotalTime();
				this.stopNoFaceTime();
			}
			return false;
		} finally {
			this.polInvalidFrameRecoveryInProgress = false;
		}
	}

	private availableToLogRecoveryFailure(): boolean {
		return (
			!this.operation.config.tutorial && !this.pageWarning.show && !this.handsetLandscape && !this.loading.show
		);
	}

	/**
	 * Marca la operación actual como completada y actualiza el estado de la operación de tipo FINISH.
	 *
	 * @param status El estado de la operación que se debe establecer en la operación de tipo FINISH.
	 */
	public nextOperation(status: OPERATION_STATUS, process: IOperation[]): void {
		const ocrOperation = process?.find((s: IOperation) => s.type === OPERATION_TYPE.OCR);
		if (process && ocrOperation) {
			if (!ocrOperation.completed) {
				// Reintento de OCR pendiente
				this.backToOcrPendingErrorCode = ocrOperation.pending_error_code ?? null;
				this.setPageWarning(PAGE_WARNING.BACK_TO_OCR);
				if (String(this.backToOcrPendingErrorCode) === ERROR_CODE.NO_FACE_IN_DOCUMENT) {
					this.tts.speak('OCR.FRONT.INSTRUCTIONS.TEXT.ONWARNING', true);
					this.tts.speak('OCR.AUDIO.IS_BLURRY_1', true);
				}
				this.stopTotalTime();
				setTimeout(() => {
					this.setPageWarning(null, false);
					this.operation.completed = true;
					this.operationService.setOperations(process);
				}, POL_BACK_TO_OCR_TIME * 1000);
				return;
			} else if (ocrOperation.state === OPERATION_STATUS.KO) {
				// OCR fallido
				this.verificationFail(TEST_STATUS.KO);
				return;
			}
		}

		this.operation.completed = true;
		const found = this.operations.find((s: IOperation) => s.type === OPERATION_TYPE.FINISH);
		if (found) {
			(found as IFinishOperation).config.status = status;
		}
	}

	get backToOcrTitleKey(): string {
		return String(this.backToOcrPendingErrorCode) === ERROR_CODE.NO_FACE_IN_DOCUMENT
			? 'OCR.FRONT.INSTRUCTIONS.TEXT.ONWARNING'
			: 'LIFE_CHECK.PAGE_WARNING.BACK_TO_OCR.TITLE';
	}

	get backToOcrShowSubtitle(): boolean {
		return String(this.backToOcrPendingErrorCode) !== ERROR_CODE.NO_FACE_IN_DOCUMENT;
	}

	private startWebcam(): void {
		if (this.permissionsCamera && !this.cameraService.hasGetUserMedia()) {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.cloudLogsService.sendCloudLogs(
				{ getUserMedia: 'getUserMedia is not supported by your browser' },
				PolLog.PRELOAD,
				LogType.Error,
			);
			this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
		}
	}

	/*
	 * Service enviar datos
	 */
	private sendResult(valid: boolean, status: TEST_STATUS = TEST_STATUS.OK): void {
		if (!valid) {
			this.polLiveDebugFrame = this.polLiveDebugFrame ?? this.capturePolDebugFrameForKo();
		}
		// Actualizamos el tiempo de finalización
		this.lifeCheckConf.general.finishAt = new Date().toISOString();
		// Detenemos el timer
		this.subscriptionTimers.unsubscribe();
		// Activamos el loading
		this.activateLoading();
		// Añadimos los datos de la prueba de vida al resultado
		this.addDataToReturn();

		const body = this.getBodyResult(valid, status);

		/* Lanzamos petición al servidor.
		 * Si responde OK, se continúa con el siguiente paso.
		 * Si responde KO, mandar al FINISH.
		 */
		this.lifeCheckService.sendLifeCheckResult(body).subscribe({
			next: (response: IPolResponse | any) => {
				// TODO: gestionar de forma correcta
				if (response.result === OPERATION_STATUS.OK) {
					// Notificar prueba de vida completada
					this.identitySDKService.notifyOperationSuccess(OperationType.POL, {
						status: status,
						result: response.result || response.response,
						response: response,
					});
					this.nextOperation(OPERATION_STATUS.OK, response.process);
				} else {
					const error = { error: response as unknown as IPolErrorResponse };
					this.handleSendResultError(error, status);
				}
			},
			error: (error: { error: IPolErrorResponse }) => {
				this.handleSendResultError(error, status);
			},
			complete: () => {
				this.loading.show = false;
				this.screenShotService.stopSaveScreenshot();
			},
		});
	}

	/**
	 * Maneja los errores que se producen al enviar el resultado de la verificación de vida.
	 *
	 * @param error El error recibido de la respuesta del servicio de verificación de vida
	 * @param status El estado de la verificación de vida en el momento del error
	 * @returns void
	 */
	private handleSendResultError(error: { error: IPolErrorResponse }, status: TEST_STATUS): void {
		// Notificar error al SDK
		this.identitySDKService.notifyOperationError(OperationType.POL, error?.error?.error?.msg || 'POL Error', {
			status,
			errorCode: error?.error?.error?.error_code,
		});

		// Error general
		if (this.errorService.hasGeneralError({ ...error?.error })) {
			this.screenShotService.stopSaveScreenshot();
			return;
		}

		// 2099 / storage_retry: interceptor agotó reintentos; no marcar POL como KO definitivo.
		if (isStorageRetryError(error?.error)) {
			this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
			this.loading.show = false;
			this.screenShotService.stopSaveScreenshot();
			return;
		}

		// Error controlado
		const errorCode: ERROR_CODE = error?.error?.error?.error_code;
		if (errorCode) {
			if (
				this.operation.config.mode === MODE.AUTO &&
				this.operation.config?.fallback?.type === MODE.MANUAL &&
				(status === TEST_STATUS.KO ||
					status === TEST_STATUS.KO_MAXTRIES ||
					status === TEST_STATUS.KO_HELP ||
					errorCode === ERROR_CODE.FALLBACK_MANUAL)
			) {
				this.operation.config.mode = MODE.MANUAL;
				return;
			}

			const retry = error?.error?.retry ?? false;

			if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
				this.verificationFail(TEST_STATUS.KO);
				return;
			}
			this.verificationFail(status);
		} else {
			// Error no controlado
			// TODO: estandarizar
			this.handleUncaughtError(status);
		}
		this.screenShotService.stopSaveScreenshot();
	}

	/**
	 * Maneja errores no capturados durante el proceso de verificación de vida.
	 *
	 * Esta función muestra una advertencia de error en el servidor, detiene el indicador de carga,
	 * envía los logs correspondientes a la nube y, tras un tiempo determinado, notifica al iframe
	 * que el resultado ha sido cancelado por error.
	 *
	 * @param status El estado de la verificación de vida en el momento del error.
	 */
	private handleUncaughtError(status: TEST_STATUS): void {
		this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
		this.loading.show = false;
		this.cloudLogsService.sendCloudLogs(
			{ status: status, observations: PAGE_WARNING.SERVER_ERROR },
			PolLog.FINISH,
			LogType.Error,
		);
		timer(POL_FINISH_TIME * 1000).subscribe(() => {
			this.sendDataIframe(IFRAME_RESULT.CANCEL, 'Error: sendResult');
		});
	}

	/**
	 * Maneja el fallo en la verificación de vida.
	 *
	 * Esta función reproduce un mensaje de texto a través de TTS, envía los logs correspondientes a la nube,
	 * muestra una advertencia en la página y, tras un tiempo determinado, envía el resultado KO al iframe.
	 *
	 * @param status El estado de la verificación de vida que ha fallado.
	 */
	private verificationFail(status: TEST_STATUS): void {
		this.operation.completed = true;
		this.cloudLogsService.sendCloudLogs(
			{ status: status, observations: PAGE_WARNING.VERIFICATION_FAIL },
			PolLog.FINISH,
			LogType.Warning,
		);
		this.processService.unSubcribeCheckStatus();
		this.operations.forEach((op: IOperation) => {
			if (op.type !== OPERATION_TYPE.FINISH) {
				op.completed = true;
			}
		});
		const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		if (foundFinish) {
			(foundFinish as IFinishOperation).config.status =
				status === TEST_STATUS.OK ? OPERATION_STATUS.OK : OPERATION_STATUS.KO;
		} else {
			this.setPageWarning(PAGE_WARNING.VERIFICATION_FAIL);
			timer(POL_FINISH_TIME * 1000).subscribe(() => {
				this.sendDataIframe(IFRAME_RESULT.KO);
			});
		}
	}

	/**
	 * Genera el cuerpo del resultado para el proceso de verificación de vida.
	 *
	 * @param valid Indica si la verificación de vida fue válida.
	 * @param status Estado actual de la verificación de vida.
	 * @returns Un objeto `IPolPayload` que contiene las imágenes capturadas, las cámaras utilizadas,
	 *          los datos serializados del resultado y el estado de validez.
	 */
	private getBodyResult(valid: boolean, status: TEST_STATUS): IPolPayload {
		const images: Array<string> = [];
		const images_pol_proof: Array<string | null> = [];

		this.lifeCheckConf.middleFace.photos.forEach((p: { img: string; label: string }) => {
			images.push(p.img);
			images_pol_proof.push(LIFE_TEST_ID.MIDDLEFACE);
		});
		this.lifeCheckConf.lifeTest.forEach((lifeTest: ILifeTest) => {
			if (lifeTest.photos.length) {
				lifeTest.photos.forEach((p: { img: string; label: string }) => {
					images.push(p.img);
					images_pol_proof.push(lifeTest.lifeTestId);
				});
			}
		});

		// Usar allCamerasValue para obtener el valor actual sincronizadamente
		// Esto asegura que siempre tengamos las cámaras disponibles cuando se envía el resultado
		let camerasToUse: MediaDeviceInfo[] = this.allCameras;
		if (this.cameraService.allCamerasValue && this.cameraService.allCamerasValue.length > 0) {
			camerasToUse = this.cameraService.allCamerasValue;
		}
		const cameras: Array<string> = camerasToUse.map((c: MediaDeviceInfo) => c.label);

		if (!valid) {
			this.koDebugImagesService.sendPolKoDebugLogs(
				valid,
				status,
				this.lifeCheckConf,
				this.polLiveDebugFrame ?? this.imageData16x9,
				{
					operationType: this.operation?.type,
					phase: 'final',
				},
			);
			this.polLiveDebugFrame = null;
		}

		return {
			images,
			images_pol_proof,
			cameras,
			device_id: this.auth.getDeviceId(),
			data: JSON.stringify([...this.resultToReturn, { status: status }]),
			status: valid,
		};
	}

	/*
	 * Controles de la cámara
	 */
	public onCameraError(err: any): void {
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
		this.hidePreviewLoading();
		const now = Date.now();
		if (now - this.lastPolCameraErrorLogAt >= 5000) {
			this.lastPolCameraErrorLogAt = now;
			this.cloudLogsService.sendCloudLogs(
				{
					onCameraError: {
						name: err?.name,
						message: err?.message,
					},
				},
				PolLog.PRELOAD,
				LogType.Error,
			);
		}
		if (this.isWithinCameraChangeGrace() || this.polCameraSwitchInProgress) {
			return;
		}
		if (err?.name === 'NotAllowedError') {
			if (!this.browserstack) {
				this.permissionsCamera = false;
				this.updateSubtitle();
			}
			this.onPermissionsDenied();
		} else {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.stopTotalTime();
			this.startWarningTime();
		}
	}

	public onCameraPermissionRevoked(): void {
		if (!this.permissionsCamera && !this.cameraPermissionsReady) {
			return;
		}
		this.stopPolCaptureLoop();
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
		this.hidePreviewLoading();
		this.cloudLogsService.sendCloudLogs({ onCameraPermissionRevoked: true }, PolLog.IN_PROCESS, LogType.Error);
		if (!this.browserstack) {
			this.permissionsCamera = false;
			this.updateSubtitle();
		}
		this.onPermissionsDenied();
	}

	private stopPolCaptureLoop(): void {
		this.cancelDegradedPoll();
		if (this.animationFrame) {
			window.cancelAnimationFrame(this.animationFrame);
			this.animationFrame = 0;
		}
		if (this.video) {
			this.video.removeEventListener('loadeddata', this.predictWebcam);
		}
		this.positionFaceService.invalidateFaceLandmarks();
		this.results = undefined;
	}

	public onCameraStarted(camera: {
		stream: MediaStream;
		video: HTMLVideoElement;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	}): void {
		this.polOvalPreviewDeadStreak = 0;
		this.polOvalPreviewDeadWarned = false;
		this.polAnalysisDeadStreak = 0;
		this.polAnalysisDeadWarned = false;
		if (!this.lifeCheckConf?.middleFace?.complete) {
			this.lifeCheckConf.middleFace.startAt = '';
		}

		this.video = camera.video;
		this.canvas = camera.canvas;
		this.ctx = camera.ctx;
		this.stream = camera.stream;
		this.polCameraStartCount++;
		if (!this.video.getAttribute('data-pol-video-node-id')) {
			this.polVideoNodeSeq++;
			this.video.setAttribute('data-pol-video-node-id', `pol-video-${this.polVideoNodeSeq}`);
		}
		this.polCurrentVideoNodeId = this.video.getAttribute('data-pol-video-node-id');

		if (this.isPolStreamTrackDead()) {
			this.cloudLogsService.sendCloudLogs(
				{ polStreamDeadOnCameraStarted: { streamActive: this.stream?.active ?? false } },
				PolLog.IN_PROCESS,
				LogType.Error,
			);
			this.onCameraPermissionRevoked();
			return;
		}

		if (!this.cameraPermissionsReady) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
		} else {
			this.resetTestClocksAfterPermissionsGranted();
			this.resumeTestTimersIfApplicable();
		}

		// Añadir el event listener cuando tengamos el video, independientemente del modelo
		this.addPredictWebcamEvent();

		if (this.faceLandmarker && this.imageEmbedder) {
			void this.ensureFaceLandmarkerVideoMode();
		}

		this.getAllCameras();

		// Esperar a que el streaming esté completamente activo antes de continuar
		void this.waitForStreamingAndStartProcessing().catch(() => undefined);
	}

	/**
	 * Espera a que el streaming esté activo antes de iniciar el procesamiento de prueba de vida
	 * Si required_recording está activo y el streaming falla, ABORTA la operación (modo estricto)
	 */
	private async waitForStreamingAndStartProcessing(): Promise<void> {
		// Verificar si el streaming está habilitado en la configuración
		const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

		if (isRecordingRequired) {
			if (this.streamingManagementService.isStreamingRetryInProgress()) {
				await this.streamingManagementService.waitUntilStreamingRetryIdle();
			} else {
				try {
					// Esperar a que el streaming esté completamente activo
					await this.streamingManagementService.waitForStreamingReady();
				} catch (streamingError: any) {
					// Si required_recording está activo, el streaming es OBLIGATORIO
					this.cloudLogsService.sendCloudLogs(
						{ pol: `Streaming connection failed - Aborting operation: ${streamingError?.toString()}` },
						PolLog.IN_PROCESS,
						LogType.Error,
					);

					this.hidePolCameraLoading();

					if (!this.streamingManagementService.isStreamingRetryInProgress()) {
						// Mostrar pantalla de error con botón de reintento
						this.errorService.setStreamingError(async () => {
							await this.streamingManagementService.retryStreaming(() => this.errorService.clearError());
						});
					}
					throw streamingError;
				}
			}
		}

		try {
			await waitForVideoPreviewReady(this.video);

			// Ocultar el loading y permitir que el procesamiento comience
			this.hidePolCameraLoading();
		} catch (error: any) {
			// Error crítico no relacionado con streaming
			this.cloudLogsService.sendCloudLogs(
				{ pol: `Critical error in waitForStreamingAndStartProcessing: ${error?.toString()}` },
				PolLog.IN_PROCESS,
				LogType.Error,
			);
			this.hidePolCameraLoading();
		}
	}

	private hidePolCameraLoading(): void {
		if (this.operation?.config?.iframe) {
			this.loadingService.hideIframe();
		} else {
			this.loadingService.hide();
		}
		this.hidePreviewLoading();
	}

	private activatePreviewLoading(textKey: string): void {
		this.loading = {
			show: true,
			text: this.trans.instant(textKey),
		};
	}

	private hidePreviewLoading(): void {
		const wasLoading = this.loading.show;
		this.loading = { show: false, text: '' };
		if (wasLoading && !this.operation?.config?.tutorial) {
			this.refreshPolUiAfterCameraPreviewReady();
		}
	}

	private addPredictWebcamEvent(): void {
		if (this.video) {
			// Comprobar si existe el event listener para no añadirlo varias veces
			if (this.video.hasAttribute('data-loadeddata-listener')) return;
			// Añadir atributo para identificar que ya tiene el event listener
			this.video.setAttribute('data-loadeddata-listener', 'true');

			// Eliminar el listener anterior y añadir el nuevo
			this.video.removeEventListener('loadeddata', this.predictWebcam);
			// Añadir el nuevo listener
			this.video.addEventListener('loadeddata', this.predictWebcam);
		}
	}

	private getAllCameras(): void {
		this.cameraService.getAllCameras();
	}

	/**
	 * Misma condición que el bloque que ejecuta scoring + `lifeTestCycle` en `predictWebcam`.
	 */
	private isPolLivenessProcessingFrame(): boolean {
		return (
			!!this.lifeCheckConf?.general &&
			this.lifeCheckConf.general.status !== TestStatus.OnComplete &&
			this.lifeCheckConf.general.status !== TestStatus.OnFail &&
			this.lifeCheckConf.general.status !== TestStatus.OnWarning &&
			!this.loading.show &&
			!this.operation.config.tutorial &&
			!this.pageWarning.show &&
			!this.handsetLandscape
		);
	}

	/**
	 * Fija `middleFace.startAt` tras el primer `detectForVideo` con stream válido
	 * y canvas de análisis ya pintado (`syncPolAnalysisCanvasFromVideo` en el mismo frame).
	 */
	private ensureMiddleFaceStartAtAfterDetect(): void {
		if (this.lifeCheckConf.middleFace.startAt) return;
		if (!this.video || !this.canvas) return;
		const vw = this.video.videoWidth;
		const vh = this.video.videoHeight;
		if (vw <= 0 || vh <= 0) return;
		if (this.canvas.width <= 0 || this.canvas.height <= 0) return;

		this.lifeCheckConf.middleFace.startAt = new Date().toISOString();
	}

	/**
	 * Adaptación dinámica de la frecuencia de `lifeTestCycle` según el coste del callback `predictWebcam`.
	 *
	 * **Idea:** `detectForVideo`, subtítulos y scoring siguen en (casi) cada frame; solo la máquina de
	 * estados (centrado, fases, `match` vía `lifeTestCycle`) es costosa. Si Chrome marca handlers de rAF
	 * lentos (p. ej. avisos tipo "handler took N ms"), subimos N para ejecutar esa máquina menos veces
	 * por segundo y aliviar el hilo principal (NIF-81).
	 *
	 * **Métrica:** `handlerDurationMs` es el tiempo desde el inicio del tramo medido hasta justo antes
	 * del siguiente `requestAnimationFrame`. Umbrales alineados con el comportamiento típico del
	 * navegador (~50 ms como "frame largo").
	 *
	 * **Histéresis:** Racha de frames "lentos" sube N; racha de frames "rápidos" baja N; la banda
	 * intermedia (entre FAST y SLOW) resetea rachas para no oscilar.
	 *
	 * **Ámbito:** Solo aplica cuando `isPolLivenessProcessingFrame()` (mismo contexto que el scoring POL:
	 * sin tutorial, sin pantallas de ayuda, etc.); fuera de ese modo se resetean rachas y no se toca N.
	 */
	private adjustLifeTestCycleAdaptiveInterval(handlerDurationMs: number): void {
		if (!this.isPolLivenessProcessingFrame()) {
			this.polSlowFrameStreak = 0;
			this.polFastFrameStreak = 0;
			return;
		}
		// Varias ejecuciones seguidas por encima del umbral "lento" → subir N (menos ciclos por segundo).
		if (handlerDurationMs > POL_SLOW_FRAME_THRESHOLD_MS) {
			this.polFastFrameStreak = 0;
			this.polSlowFrameStreak++;
			if (
				this.polSlowFrameStreak >= POL_SLOW_STREAK_TO_INCREASE_N &&
				this.lifeTestCycleEveryNFrames < POL_LIFE_TEST_CYCLE_MAX_FRAMES
			) {
				this.lifeTestCycleEveryNFrames++;
				this.lifeTestCycleFrameCounter = 0;
				this.polSlowFrameStreak = 0;
			}
		} else if (handlerDurationMs < POL_FAST_FRAME_THRESHOLD_MS) {
			// Dispositivo recuperando fluidez: bajar N hacia el mínimo tras varios frames rápidos seguidos.
			this.polSlowFrameStreak = 0;
			this.polFastFrameStreak++;
			if (
				this.polFastFrameStreak >= POL_FAST_STREAK_TO_DECREASE_N &&
				this.lifeTestCycleEveryNFrames > POL_LIFE_TEST_CYCLE_MIN_FRAMES
			) {
				this.lifeTestCycleEveryNFrames--;
				this.lifeTestCycleFrameCounter = 0;
				this.polFastFrameStreak = 0;
			}
		} else {
			// Zona intermedia: no acumular subida ni bajada para evitar ping-pong.
			this.polSlowFrameStreak = 0;
			this.polFastFrameStreak = 0;
		}
	}

	private canContinueCameraFrameLoop(): boolean {
		return !!this.video && this.permissionsCamera && this.canRunTestTimers();
	}

	private cancelDegradedPoll(): void {
		if (this.polDegradedPollTimeoutId !== null) {
			clearTimeout(this.polDegradedPollTimeoutId);
			this.polDegradedPollTimeoutId = null;
		}
	}

	private syncPreviewBufferSnapshot(context: string): void {
		this.polPreviewDiagnosticsBuffer.updateVideoSnapshot({
			context,
			...this.getPreviewDiagnosticsSnapshot(this.video),
		});
	}

	private flushPolPreviewDiagnosticsBuffer(flushReason: string, logLevel: LogType = LogType.Warning): void {
		if (!this.polPreviewDiagnosticsBuffer.isDegraded()) {
			return;
		}
		this.cloudLogsService.sendCloudLogs(
			this.polPreviewDiagnosticsBuffer.buildSummary({ flushReason }),
			PolLog.IN_PROCESS,
			logLevel,
		);
		this.polPreviewDiagnosticsBuffer.flushFrameCounters();
	}

	private maybeFlushPolPreviewDiagnosticsBuffer(flushReason: string): void {
		if (!this.polPreviewDiagnosticsBuffer.shouldSendHeartbeat()) {
			return;
		}
		this.flushPolPreviewDiagnosticsBuffer(flushReason);
		this.polPreviewDiagnosticsBuffer.markHeartbeatSent();
	}

	private schedulePredictWebcamFrame(degradedPreview = false): void {
		if (!this.canContinueCameraFrameLoop()) return;

		if (degradedPreview) {
			this.polPreviewDegradedPoll = true;
			this.cancelDegradedPoll();
			this.polDegradedPollTimeoutId = setTimeout(() => {
				this.polDegradedPollTimeoutId = null;
				void this.predictWebcam();
			}, POL_DEGRADED_POLL_MS);
			return;
		}

		if (this.polPreviewDegradedPoll) {
			this.polPreviewDegradedPoll = false;
			this.cancelDegradedPoll();
			this.polPreviewDiagnosticsBuffer.markPreviewRecovered();
			this.polPreviewDiagnosticsBuffer.flushFrameCounters();
		}

		this.animationFrame = window.requestAnimationFrame(this.predictWebcam);
	}

	private predictWebcam = async () => {
		if (!this.canContinueCameraFrameLoop()) return;

		// Si los modelos no están listos aún, continuar al siguiente frame
		if (!this.faceLandmarker || !this.imageEmbedder) {
			this.schedulePredictWebcamFrame();
			return;
		}

		// Ensure video is ready
		if (!this.canRunPolDetectionOnCurrentFrame()) {
			this.polPreviewDiagnosticsBuffer.recordZeroDimFrame();
			this.syncPreviewBufferSnapshot('videoZeroDimensions');
			this.maybeFlushPolPreviewDiagnosticsBuffer('zeroDimensionsHeartbeat');
			if (!this.isPolLivenessProcessingFrame()) {
				this.schedulePredictWebcamFrame(true);
				return;
			}
			void this.handleUnusableFrame('videoZeroDimensions');
			this.schedulePredictWebcamFrame(true);
			return;
		}

		const layoutReady = this.isCurrentFrameUsable();
		if (!layoutReady) {
			if (this.isPolLivenessProcessingFrame()) {
				this.maybeFlushPolPreviewDiagnosticsBuffer('invalidPreviewHeartbeat');
				void this.handleUnusableFrame('previewRectNotUsable');
			} else {
				this.schedulePredictWebcamFrame(true);
				return;
			}
		}
		this.polInvalidFrameConsecutiveCount = 0;
		this.polInvalidFrameRecoveryFailures = 0;

		// STABILITY CHECK: After camera change, wait for dimensions to stabilize
		const currentDimensions = { width: this.video.videoWidth, height: this.video.videoHeight };
		const timeSinceCameraChange = Date.now() - this.cameraChangeTime;

		if (timeSinceCameraChange < 2000) {
			// Still within the stabilization window after camera change
			if (this.lastVideoDimensions) {
				if (
					this.lastVideoDimensions.width === currentDimensions.width &&
					this.lastVideoDimensions.height === currentDimensions.height
				) {
					this.videoDimensionStableCount++;
					// Require 5 consecutive frames with same dimensions
					if (this.videoDimensionStableCount < 5) {
						this.schedulePredictWebcamFrame();
						return;
					}
				} else {
					// Dimensions changed, reset counter
					this.videoDimensionStableCount = 0;
				}
			}
			this.lastVideoDimensions = currentDimensions;
		} else {
			// After 2 seconds, reset the stabilization check
			this.videoDimensionStableCount = 0;
			this.lastVideoDimensions = null;
		}

		const frameStart = performance.now();

		// Now let's start detecting the stream.
		await this.ensureFaceLandmarkerVideoMode();
		const startTimeMs = performance.now();
		this.polDetectRanThisFrame = false;
		if (this.lastVideoTime !== this.video.currentTime) {
			const msSinceLastDetect = startTimeMs - this.lastPolDetectCpuTimeMs;
			if (msSinceLastDetect >= POL_DETECT_MIN_INTERVAL_MS) {
				this.lastVideoTime = this.video.currentTime;
				this.lastPolDetectCpuTimeMs = startTimeMs;
				this.polDetectRanThisFrame = true;
				this.syncPolAnalysisCanvasFromVideo();
				this.results = this.faceLandmarker.detectForVideo(this.canvas, startTimeMs);
				this.ensureMiddleFaceStartAtAfterDetect();
			}
		}
		const faceLandmarks = this.results?.faceLandmarks;
		if (faceLandmarks?.length) {
			if (this.polDetectRanThisFrame) {
				this.positionFaceService.setFacePoints(faceLandmarks[0], this.canvas);
			}
			if (this.positionFaceService.getDraw()) {
				this.positionFaceService.drawControl(this.ctx);
			}
		} else {
			// Sin cara clara (sin landmarks): no arrastrar el último resultado MediaPipe ni puntos de la cara del frame anterior.
			this.positionFaceService.invalidateFaceLandmarks();
			this.results = undefined;
		}

		if (this.shouldRefreshPolUiTextsThisFrame()) {
			this.refreshPolUiTexts();
		}

		if (this.isPolLivenessProcessingFrame()) {
			const blendshapes = this.results?.faceBlendshapes ?? [];
			const hasFace = blendshapes.length > 0;
			if (hasFace) {
				this.lastFaceDetectedAtMs = Date.now();
				this.noFaceConsecutiveFrames = 0;
				const track = this.stream?.getVideoTracks?.()?.[0];
				const nosePoint = this.positionFaceService.getNosePoint();
				if (
					!this.cameraFocusService.isDetectionRefocusThrottled() &&
					track &&
					nosePoint &&
					this.video?.videoWidth &&
					this.video?.videoHeight &&
					this.canvas?.width &&
					this.canvas?.height
				) {
					const preview = this.getPreviewRect(this.video);
					const cover = resolveVideoCoverSourceRect(
						this.video.videoWidth,
						this.video.videoHeight,
						preview?.width ?? this.video.videoWidth,
						preview?.height ?? this.video.videoHeight,
						this.getEffectivePolPreviewZoomFactor(),
					);
					const focusPoint = mapCanvasPointToSensorNormalized(
						nosePoint.x,
						nosePoint.y,
						this.canvas.width,
						this.canvas.height,
						cover,
						this.video.videoWidth,
						this.video.videoHeight,
					);
					void this.cameraFocusService.triggerDetectionRefocus(track, focusPoint);
				}
			} else {
				this.noFaceConsecutiveFrames++;
			}
			blendshapes.length ? this.stopNoFaceTime() : this.startNoFaceTime();
			this.scoringFaceService.saveGeneralEmotions(blendshapes);
			this.scoringFaceService.addFullTestMatch(this.match);
			this.isHuman = hasFace;
			this.scoringFaceService.thereIsSomeBlink(
				this.scoringFaceService.getFaceBlendshapes(LIFE_TEST_ID.BLINK, blendshapes),
				this.lifeCheckConf.general.minPercenteageBlink,
				this.controls,
			);
			this.lifeTestCycleFrameCounter++;
			if (this.lifeTestCycleFrameCounter % this.lifeTestCycleEveryNFrames === 0) {
				this.isThereMoreThanOneFace();
				void this.lifeTestCycle();
			}
		}

		this.adjustLifeTestCycleAdaptiveInterval(performance.now() - frameStart);

		// Call this function again to keep predicting when the browser is ready.
		this.schedulePredictWebcamFrame();
	};

	/*
	 * Control de cada prueba de vida
	 */
	private async lifeTestCycle(): Promise<void> {
		// Pausa del motor de liveness mientras se muestra el reinicio (hasta repeatAllProcess)
		if (this.lifeCheckConf?.general?.status === TestStatus.OnWarning) {
			return;
		}

		// Iniciamos por primera vez la prueba de vida
		this.initializeTestIfNeeded();

		// Early return si la prueba de vida general ha fallado
		if (this.handleGeneralFailure()) return;

		// FASE 1: Encuadrar la cara
		if (this.shouldProcessMiddleFace()) {
			await this.processMiddleFacePhase();
			return;
		}

		// FASE 2: Pruebas de vida
		if (this.shouldProcessLifeTests()) {
			await this.processLifeTestPhase();
			return;
		}

		// FASE 3: Últimas validaciones
		if (this.shouldProcessFinalValidation()) {
			this.finishValidation();
			return;
		}
	}

	// Validaciones para el centrado de la cara
	private async middleFaceValidation(): Promise<void> {
		// Obtenemos el estado actual
		const { status } = this.lifeCheckConf.middleFace;

		// FASE 1: Estado OnFail
		if (status === TestStatus.OnFail) return;

		// Centrado ya cerrado: no reabrir ventana de validación antes de la primera prueba de gesto.
		if (status === TestStatus.OnComplete || this.lifeCheckConf.middleFace.complete) return;

		// FASE 1: Estado OnWarning
		if (status === TestStatus.OnWarning) {
			this.processMiddleFaceWarning();
		}

		// FASE 1: Estado OnInit
		if (status === TestStatus.OnInit) {
			this.processMiddleFaceInit();
		}

		// FASE 1: Recopilamos resultados solo con inferencia fresca (evita scoring obsoleto).
		// Entre frames sin `detectForVideo`, `match` puede quedar desfasado respecto al vídeo.
		if (this.polDetectRanThisFrame) {
			await this.collectMiddleFaceResults();
		}
	}

	private isMiddleFaceValid(): boolean {
		this.scoringFaceService.calcMiddleFaceScoring(this.lifeCheckConf.middleFace, false);
		return (
			this.scoringFaceService.getPercentageTimeInMiddle(
				this.scoringFaceService.getValidationMatch(),
				this.controls,
			) >= this.lifeCheckConf.general.minPercenteageTimeInMiddle &&
			this.scoringFaceService.getSimilarity().environment >
				this.lifeCheckConf.general.minEnvironmentSimilarityPercenteage &&
			this.scoringFaceService.getSimilarity().face > this.lifeCheckConf.general.minFaceSimilarityPercenteage
		);
	}

	// Validaciones para las pruebas de vida
	private async lifeTestValidation(lifeTest: ILifeTest): Promise<void> {
		// FASE 2: Seteamos el tiempo de inicio
		this.initializeLifeTestStartTime(lifeTest);

		// Obtenemos el estado actual
		const { status } = lifeTest;

		// FASE 2: Estado OnFail
		if (status === TestStatus.OnFail) return;

		// Prueba ya cerrada: no reabrir ventana de validación ni timers (p. ej. pausa entre sonrisa y giro).
		if (status === TestStatus.OnComplete || lifeTest.complete) return;

		// FASE 2: Prueba estado OnWarning
		if (lifeTest.status === TestStatus.OnWarning) {
			this.processLifeTestWarning(lifeTest);
		}

		// FASE 2: Prueba estado OnInit
		if (lifeTest.status === TestStatus.OnInit) {
			this.processLifeTestInit(lifeTest);
		}

		// FASE 2: Recopilamos resultados
		await this.collectLifeTestResults(lifeTest);
	}

	private completeLifeTest(lifeTest: ILifeTest): void {
		this.completingPolTestId = lifeTest.lifeTestId;
		lifeTest.complete = true;
		this.lifeTestValidationStartedAtMs = null;
		this.activeLifeTestMinTimeGate = null;
		this.nosePath = [];
		this.resetControls();
		this.scoringFaceService.setValidationEmotion([]);
		this.scoringFaceService.setLifeTestEmotion([]);
		if (lifeTest.lifeTestId === LIFE_TEST_ID.APPROACHFACE) {
			this.resetApproachFaceSession();
		}
	}

	private startInterTestTransition(): void {
		this.interTestTransition = { phase: 'success', startedAtMs: Date.now() };
		this.polUiLifeTestId = null;
	}

	private getInterTestSuccessFeedbackMs(): number {
		const configuredMs = (this.lifeCheckConf?.general?.timeBetweenTest ?? 0) * 1000;
		return configuredMs > 0 ? configuredMs : POL_SUCCESS_FEEDBACK_FALLBACK_MS;
	}

	/**
	 * Mantiene feedback de éxito y exige recentrado entre pruebas consecutivas.
	 * @returns true si hay que pausar el arranque de la siguiente prueba
	 */
	private handleInterTestTransition(): boolean {
		if (!this.interTestTransition) {
			return false;
		}

		if (this.interTestTransition.phase === 'success') {
			const elapsedMs = Date.now() - this.interTestTransition.startedAtMs;
			if (elapsedMs < this.getInterTestSuccessFeedbackMs()) {
				return true;
			}
			this.interTestTransition = { phase: 'recenter', startedAtMs: Date.now() };
			this.resetAnimations();
		}

		this.positionFaceService.setControlRect(LIFE_TEST_ID.SMILE, this.canvas);
		const centered = this.positionFaceService.centerMatch();
		this.updateMatch(centered);
		this.done = this.matchUi;
		if (!centered) {
			return true;
		}

		this.interTestTransition = null;
		this.completingPolTestId = null;
		this.polAudioHistoryService.clearDataHistory();
		return false;
	}

	/** Prueba de gesto activa: excluye las ya cerradas en OnComplete aunque `complete` llegue tarde. */
	private getCurrentPendingLifeTest(): ILifeTest | undefined {
		return this.lifeCheckConf.lifeTest.find(
			(lt) => !lt.complete && lt.status !== TestStatus.OnComplete && lt.status !== TestStatus.OnFail,
		);
	}

	/** Al cambiar de prueba, limpia el check verde de la anterior (timer check dura ~1 s). */
	private prepareScanUiForLifeTest(lifeTest: ILifeTest): void {
		if (this.polUiLifeTestId === lifeTest.lifeTestId) {
			return;
		}
		this.completingPolTestId = null;
		this.polAudioHistoryService.clearDataHistory();
		this.polUiLifeTestId = lifeTest.lifeTestId;
		this.resetAnimations();
	}

	private isLifeTestValid(lifeTest: ILifeTest): boolean {
		this.scoringFaceService.calcLifeTestScoring(lifeTest, false);
		const elapsedSinceValidationStartMs =
			this.lifeTestValidationStartedAtMs == null ? 0 : Date.now() - this.lifeTestValidationStartedAtMs;
		const canRelaxTransitions =
			this.lifeTestValidationStartedAtMs != null &&
			elapsedSinceValidationStartMs >= lifeTest.minTime * POL_MIN_TIME_MS_PER_SECOND;
		const requiredTransitions = canRelaxTransitions
			? Math.max(1, lifeTest.minTransitionsFaceBlendshape - 1)
			: lifeTest.minTransitionsFaceBlendshape;
		return (
			this.scoringFaceService.areHeadMovementsValid(lifeTest, this.nosePath, this.controls) &&
			this.scoringFaceService.getPercentageTimeInMiddle(
				this.scoringFaceService.getValidationMatch(),
				this.controls,
			) >= this.lifeCheckConf.general.minPercenteageTimeInMiddle &&
			this.scoringFaceService.getPercentageLifeTestEmotion(
				this.scoringFaceService.getValidationEmotion(),
				this.controls,
			) > lifeTest.minPercentageFaceBlendshape &&
			this.scoringFaceService.getLifeTestTransition(
				this.scoringFaceService.getLifeTestEmotion(),
				this.controls,
			) >= requiredTransitions &&
			this.scoringFaceService.getSimilarity().environment >
				this.lifeCheckConf.general.minEnvironmentSimilarityPercenteage &&
			this.scoringFaceService.getSimilarity().face > this.lifeCheckConf.general.minFaceSimilarityPercenteage
		);
	}

	/**
	 * Evaluación final POL (similitud fotos + scoring general).
	 */
	private async computePolFinalValidation(): Promise<{ valid: boolean; breakdown: Record<string, unknown> }> {
		const percentageSimilarityPhotos: {
			similarity?: Array<ISimilarity>;
			result: number;
		} = await this.getSimilarityAllLifeTestPhotos();
		this.scoringFaceService.calcGeneralScoring(
			this.lifeCheckConf,
			this.timerManagerService.totalTime,
			percentageSimilarityPhotos,
		);
		const minTotal = this.lifeCheckConf.general.minTotalSimilarityPercenteage;
		const minScore = this.lifeCheckConf.general.minScoring;
		const totalSim = percentageSimilarityPhotos.result;
		const scoreRes = this.lifeCheckConf.general.scoring.result;
		const valid = totalSim > minTotal && scoreRes > minScore;

		const breakdown: Record<string, unknown> = {
			passTotalSimilarity: totalSim > minTotal,
			passMinScoring: scoreRes > minScore,
			totalSimilarityPct: totalSim,
			minTotalSimilarityPct: minTotal,
			generalScoringResult: scoreRes,
			minScoring: minScore,
		};
		return { valid, breakdown };
	}

	// Evaluamos el número de intentos que lleva el usuario para ver si repetimos la prueba o la marcamos como incompleta
	private generalTries(observations?: string, hiddenMessage?: boolean): void {
		this.subtitle = '';

		this.activateLoading();
		// Cancela timers diferidos del intento anterior (p. ej. minTime en collectLifeTestResults, completeLifeTest)
		// para que no muten estado durante OnWarning ni tras el primer reinicio.
		this.resetTimers();
		if (observations) {
			this.lifeCheckConf.general.observations.push(observations);
		}
		const countBefore = this.lifeCheckConf.general.countTries;
		this.lifeCheckConf.general.countTries++;
		if (this.lifeCheckConf.general.countTries > this.lifeCheckConf.general.tries) {
			this.cloudLogsService.sendCloudLogs(
				{
					polGeneralTries: {
						outcome: 'maxTriesKo',
						observations: observations ?? null,
						countBefore,
						countAfter: this.lifeCheckConf.general.countTries,
						...this.buildPolAttemptDiagContext(),
					},
				},
				PolLog.IN_PROCESS,
				LogType.Warning,
			);
			this.lifeCheckConf.general.status = TestStatus.OnFail;
			this.sendResult(false, TEST_STATUS.KO_MAXTRIES);
		} else {
			this.cloudLogsService.sendCloudLogs(
				{
					polGeneralTries: {
						outcome: 'retry',
						observations: observations ?? null,
						hiddenMessage: !!hiddenMessage,
						timeBetweenTestSec: this.lifeCheckConf.general.timeBetweenTest,
						countBefore,
						countAfter: this.lifeCheckConf.general.countTries,
						...this.buildPolAttemptDiagContext(),
					},
				},
				PolLog.IN_PROCESS,
				LogType.Info,
			);
			this.loading.show = false;
			this.lifeCheckConf.general.finishAt = new Date().toISOString();
			this.lifeCheckConf.general.status = TestStatus.OnWarning;
			this.stopNoFaceTime();
			if (hiddenMessage) {
				this.repeatAllProcess();
			} else {
				this.subscriptionTimers.add(
					timer(this.lifeCheckConf.general.timeBetweenTest * 1000).subscribe(() => {
						this.repeatAllProcess();
					}),
				);
			}
		}
	}

	/**
	 * Agrega los datos de la prueba de vida al array `resultToReturn` en el formato requerido.
	 * Construye un objeto `ILifeTestDataReturn` que contiene la información de la prueba central,
	 * las pruebas de vida adicionales y los datos generales, incluyendo configuraciones y resultados.
	 *
	 * Este método extrae y transforma los datos de configuración (`lifeCheckConf`) para estructurarlos
	 * correctamente antes de almacenarlos en el resultado final.
	 */
	private addDataToReturn(): void {
		let auxResult: ILifeTestDataReturn = {} as ILifeTestDataReturn;
		// Centrado
		auxResult = {
			lifeTest: [
				{
					lifeTestId: this.lifeCheckConf.middleFace.lifeTestId,
					status: this.lifeCheckConf.middleFace.status,
					startAt: this.lifeCheckConf.middleFace.startAt,
					finishAt: this.lifeCheckConf.middleFace.finishAt,
					scoring: this.lifeCheckConf.middleFace.scoring,
					fullScoring: this.lifeCheckConf.middleFace.fullScoring,
					photos: this.lifeCheckConf.middleFace.photos.map((p: { img: string; label: string }) => p.label),
					conf: {
						maxTime: this.lifeCheckConf.middleFace.maxTime,
						minTime: this.lifeCheckConf.middleFace.minTime,
					},
				},
			],
			general: null,
		};
		// Pruebas de vida
		this.lifeCheckConf.lifeTest.forEach((lifeTest: ILifeTest) => {
			auxResult.lifeTest.push({
				lifeTestId: lifeTest.lifeTestId,
				status: lifeTest.status,
				scoring: lifeTest.scoring,
				fullScoring: lifeTest.fullScoring,
				startAt: lifeTest.startAt,
				finishAt: lifeTest.finishAt,
				photos: lifeTest.photos.map((p: { img: string; label: string }) => p.label),
				conf: {
					maxTime: lifeTest.maxTime,
					minTime: lifeTest.minTime,
					minPercentageFaceBlendshape: lifeTest.minPercentageFaceBlendshape,
					minTransitionsFaceBlendshape: lifeTest.minTransitionsFaceBlendshape,
					noseMovement: lifeTest.noseMovement,
				},
			});
		});
		auxResult.general = {
			startAt: this.lifeCheckConf.general.startAt,
			finishAt: this.lifeCheckConf.general.finishAt,
			status: this.lifeCheckConf.general.status,
			observations: this.lifeCheckConf.general.observations,
			photos: auxResult.lifeTest.map((lt: any) => lt.photos),
			conf: {
				countTries: this.lifeCheckConf.general.countTries,
				totalLifeTest: this.lifeCheckConf.general.totalLifeTest,
				minEnvironmentSimilarityPercenteage: this.lifeCheckConf.general.minEnvironmentSimilarityPercenteage,
				minFaceSimilarityPercenteage: this.lifeCheckConf.general.minFaceSimilarityPercenteage,
				minPercenteageBlink: this.lifeCheckConf.general.minPercenteageBlink,
				minPercenteageTimeInMiddle: this.lifeCheckConf.general.minPercenteageTimeInMiddle,
				minScoring: this.lifeCheckConf.general.minScoring,
				minTotalSimilarityPercenteage: this.lifeCheckConf.general.minTotalSimilarityPercenteage,
			},
			type: this.lifeCheckConf.general.type,
			scoring: this.lifeCheckConf.general.scoring,
			operationId: this.lifeCheckConf.general.operationId,
		};
		this.resultToReturn.push(auxResult);
	}

	/*
	 * Controles de cada prueba de vida
	 */
	// Pruebas de vida pendientes
	private isThereAnyPendingLifeTest(): boolean {
		const totalLifeCheckPending: number = this.lifeCheckConf.lifeTest.filter(
			(lt: ILifeTest) => !lt.complete,
		)?.length;
		const totalLifeCheckCompleted: number = this.lifeCheckConf.lifeTest.filter(
			(lt: ILifeTest) => lt.complete,
		)?.length;
		return totalLifeCheckCompleted < this.lifeCheckConf.general.totalLifeTest && Boolean(totalLifeCheckPending);
	}

	private isLastLifeTest(): boolean {
		return this.lifeCheckConf.lifeTest.filter((lt: ILifeTest) => !lt.complete)?.length === 1;
	}

	// Repetimos todo el proceso
	private repeatAllProcess(): void {
		if (this.lifeCheckConf.general.status === TestStatus.OnWarning) {
			this.interTestTransition = null;
			this.stopNoFaceTime();
			this.resetAnimations();
			this.polUiLifeTestId = null;
			this.addDataToReturn();
			this.resetControls();
			this.resetTimers();
			this.shuffleLifeTest();
			this.nosePath = [];
			this.lifeCheckConf = {
				...this.lifeCheckConf,
				middleFace: {
					...this.lifeCheckConf.middleFace,
					status: TestStatus.OnInit,
					complete: false,
					startAt: '',
					finishAt: '',
					scoring: undefined,
					fullScoring: [],
					photos: [],
				},
				general: {
					...this.lifeCheckConf.general,
					status: TestStatus.OnInit,
					complete: false,
					scoring: undefined,
					controlValidation: false,
					finishAt: '',
					startAt: '',
					observations: [],
					messages: {
						...this.lifeCheckConf.general.messages,
						onWarning: this.originalLifeCheckConf.general.messages[TestStatus.OnWarning],
					},
				},
			};
			this.lifeCheckConf.lifeTest.forEach((lt: ILifeTest) => {
				lt.complete = false;
				lt.status = TestStatus.OnInit;
				lt.startAt = '';
				lt.finishAt = '';
				lt.photos = [];
				lt.scoring = undefined;
				lt.fullScoring = [];
			});
			this.scoringFaceService.setLifeTestMatch([]);
			this.scoringFaceService.setValidationMatch([]);
			this.scoringFaceService.setValidationEmotion([]);
			this.scoringFaceService.setLifeTestEmotion([]);
			this.resetMatchUiTracking();
			this.match = false;
			this.done = false;
			this.countMultipleface = 0;
			this.polLiveDebugFrame = null;
			this.polAnalysisDeadStreak = 0;
			this.polAnalysisDeadWarned = false;
			this.updateSubtitle();
			this.completingPolTestId = null;
			this.polAudioHistoryService.clearDataHistory();
			this.alertContent = { label: '', status: 'default' };
			this.alertScanFaceText = '';
			this.resetApproachFaceSession();
			this.ensurePolCameraAfterGeneralRetry();
		}
	}

	/** Tras un reintento general, re-solicitar cámara si el stream quedó muerto (preview 0×0). */
	private ensurePolCameraAfterGeneralRetry(): void {
		if (!this.isPolStreamTrackDead() && !this.isOvalPreviewDead()) return;
		this.cameraService.resetCameraStream();
	}

	// Desde la pantalla de ayuda si le dan a reintentar se repite el proceso
	public retryAllProcess(observations: string): void {
		this.polAudioHistoryService.clearDataHistory();
		this.startTotalTime();
		if (!this.isHelpByNavHelp) this.generalTries(observations, true);
		this.isHelpByNavHelp = false;
		this.setPageWarning(null, false);
		this.stopWarningTime();
	}

	public changeDevice(): void {
		if (this.operation.config.iframe) {
			this.isVisibleChangeDevice = true;
		}
		if (!this.operation.config.iframe) {
			this.isVisibleChangeDevice = true;
		}
	}

	public checkPermissions(): void {
		this.checkCameraPermissions();
	}

	private checkCameraPermissions(): void {
		const seq = this.cameraService.beginPermissionCheck();
		// Arrancamos el reloj justo al iniciar la petición de permisos: así el KO por timeout
		// también cubre el caso en que el usuario no responde al prompt del navegador.
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
		this.operation.config.iframe
			? this.loadingService.showIframe(this.trans.instant('GENERAL.LOADING.PERMISSIONS'))
			: this.loadingService.show(this.trans.instant('GENERAL.LOADING.PERMISSIONS'));
		this.cameraService
			.resolveHasCameraPermission(this.lifeCheckService.constraints)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (hasPermission: boolean) => this.finishPermissionCheck(seq, hasPermission),
				error: (error: unknown) => this.finishPermissionCheck(seq, false, error),
			});
	}

	private finishPermissionCheck(seq: number, hasPermission: boolean, subscribeError?: unknown): void {
		if (!this.cameraService.isPermissionCheckCurrent(seq)) {
			return;
		}
		if (hasPermission) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
			this.activatePreviewLoading('GENERAL.LOADING.CAMERA_STARTING');
			setTimeout(() => {
				this.startWebcam();
			}, 500);
		} else {
			const ctx = this.cameraService.getPermissionResolveContext();
			const subErr = subscribeError as { name?: string; message?: string } | undefined;
			const now = Date.now();
			if (now - this.lastPolCameraErrorLogAt >= 5000) {
				this.lastPolCameraErrorLogAt = now;
				this.cloudLogsService.sendCloudLogs(
					{
						finishPermissionCheck: {
							from: ctx?.from ?? (subErr ? 'subscribe_error' : 'unknown'),
							errorName: ctx?.errorName ?? subErr?.name,
							errorMessage: ctx?.errorMessage ?? subErr?.message,
						},
					},
					PolLog.PRELOAD,
					LogType.Error,
				);
			}
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
			this.hidePreviewLoading();
		}
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}

	private canRunTestTimers(): boolean {
		return this.permissionsCamera && this.cameraPermissionsReady;
	}

	private onPermissionsDenied(): void {
		this.stopPolCaptureLoop();
		this.stopActiveGuidanceAudio();
		this.cameraPermissionsReady = false;
		this.cameraService.releaseActiveStream();
		this.stopTotalTime();
		this.stopWarningTime();
		this.stopNoFaceTime();
		this.stopKOHelpTime();
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
	}

	private stopActiveGuidanceAudio(): void {
		this.tts.stop();
		this.audioService.stop();
		this.stopSecondlyDataCapture();
		this.polAudioHistoryService.clearDataHistory();
	}

	private onPermissionsGranted(): void {
		this.cameraPermissionsTimerService.stopWait();
		this.cameraPermissionsReady = true;
		this.polOvalPreviewDeadStreak = 0;
		this.polOvalPreviewDeadWarned = false;
		this.polAnalysisDeadStreak = 0;
		this.polAnalysisDeadWarned = false;
		this.resetTestClocksAfterPermissionsGranted();
		this.resumeTestTimersIfApplicable();
	}

	private resetTestClocksAfterPermissionsGranted(): void {
		if (this.lifeCheckConf?.general) {
			this.lifeCheckConf.general.startAt = '';
		}
		if (this.lifeCheckConf?.middleFace && !this.lifeCheckConf.middleFace.complete) {
			this.lifeCheckConf.middleFace.startAt = '';
		}
		(this.lifeCheckConf?.lifeTest ?? []).forEach((lt: ILifeTest) => {
			if (!lt.complete) {
				lt.startAt = '';
			}
		});
		this.stopTotalTime();
		this.timerManagerService.resetTotalTime();
	}

	private resumeTestTimersIfApplicable(): void {
		if (this.operation?.config?.tutorial && !this.pageWarning.show && !this.handsetLandscape) {
			this.startWarningTime();
		}
		if (!this.handsetLandscape && !this.pageWarning.show) {
			this.startTotalTime();
		}
	}

	private onCameraPermissionsTimeout(): void {
		// `cameraPermissionsReady` solo pasa a true en onPermissionsGranted(): si seguimos aquí,
		// el usuario no ha concedido permisos (haya respondido NO o no haya respondido al prompt).
		if (this.cameraPermissionsReady) {
			return;
		}
		this.cloudLogsService.sendCloudLogs(
			{
				onCameraPermissionsTimeout: true,
				status: TEST_STATUS.KO_PERMISSION,
			},
			PolLog.FINISH,
			LogType.Error,
		);
		this.sendResult(false, TEST_STATUS.KO_PERMISSION);
	}

	// Orden de las pruebas
	private shuffleLifeTest(): void {
		const types = {
			[POL_TYPE.RANDOM]: () =>
				(this.lifeCheckConf.lifeTest = this.allLifeTest
					.sort(() => Math.random() - 0.5)
					.slice(0, this.lifeCheckConf.general.totalLifeTest)),
			[POL_TYPE.RANDOM_REPEAT]: () => {
				const lifeTests: Array<ILifeTest> = [];
				while (lifeTests.length < this.lifeCheckConf.general.totalLifeTest) {
					const selected: ILifeTest = JSON.parse(
						JSON.stringify(this.allLifeTest[Math.floor(Math.random() * this.allLifeTest.length)]),
					);
					const foundSmile = lifeTests.find((t: ILifeTest) => t.lifeTestId === LIFE_TEST_ID.SMILE);
					if (foundSmile && selected.lifeTestId === LIFE_TEST_ID.SMILE) {
						continue;
					}
					lifeTests.push(selected);
				}
				this.lifeCheckConf.lifeTest = JSON.parse(JSON.stringify(lifeTests));
			},
			[POL_TYPE.LINEAL]: () =>
				(this.lifeCheckConf.lifeTest = this.allLifeTest.slice(0, this.lifeCheckConf.general.totalLifeTest)),
		};
		types[this.lifeCheckConf.general.type]();
	}

	// Hay mas de una cara
	private isThereMoreThanOneFace(): void {
		if (this.isMultipleFacesStartCooldownActive()) {
			this.countMultipleface = 0;
			return;
		}

		const landmarkCount = this.results?.faceLandmarks?.length ?? 0;
		if (landmarkCount < 2) {
			this.countMultipleface = 0;
			return;
		}

		if (!this.hasConfirmedMultipleFaces()) {
			this.countMultipleface = 0;
			return;
		}
		this.countMultipleface++;
		if (this.countMultipleface >= 5) {
			this.countMultipleface = 0;
			this.scoringFaceService.setSimultaneousFaces(landmarkCount);
			if (this.lifeCheckConf.general.countTries + 1 > this.lifeCheckConf.general.tries) {
				this.lifeCheckConf.general.status = TestStatus.OnFail;
				this.sendResult(false, TEST_STATUS.KO_MULTIPLE_FACES);
				this.stopTotalTime();
			} else {
				this.setPageWarning(PAGE_WARNING.MULTIPLE_FACES);
				this.stopTotalTime();
				this.startWarningTime();
			}
		}
	}

	private hasConfirmedMultipleFaces(): boolean {
		const landmarkFaces = this.results?.faceLandmarks ?? [];
		const blendshapeFaces = this.results?.faceBlendshapes ?? [];
		if (landmarkFaces.length < 2 || blendshapeFaces.length < 2) {
			return false;
		}

		const canvasWidth = this.canvas?.width ?? 0;
		const canvasHeight = this.canvas?.height ?? 0;
		if (!canvasWidth || !canvasHeight) {
			return false;
		}

		const minArea = canvasWidth * canvasHeight * POL_MULTIPLE_FACE_MIN_BBOX_AREA_RATIO;
		const bboxes = landmarkFaces
			.map((landmarks) => this.getFaceLandmarksBBox(landmarks, canvasWidth, canvasHeight))
			.filter((bbox) => bbox.area >= minArea)
			.sort((a, b) => b.area - a.area);

		if (bboxes.length < 2) {
			return false;
		}

		let geoMatch: boolean | null = null;
		if (!this.lifeCheckConf.middleFace.complete) {
			this.positionFaceService.setControlRect(LIFE_TEST_ID.MIDDLEFACE, this.canvas);
			geoMatch = this.positionFaceService.isMatch(LIFE_TEST_ID.MIDDLEFACE);
		}

		if (geoMatch === false) {
			const minSeparation = Math.min(canvasWidth, canvasHeight) * POL_MULTIPLE_FACE_MIN_CENTER_SEPARATION_RATIO;
			const separation = Math.hypot(bboxes[0].centerX - bboxes[1].centerX, bboxes[0].centerY - bboxes[1].centerY);
			if (separation < minSeparation) {
				return false;
			}
		}

		return true;
	}

	/** Sin evaluar múltiples caras hasta transcurrir el margen desde el inicio del intento PoL. */
	private isMultipleFacesStartCooldownActive(): boolean {
		const startAt = this.lifeCheckConf.general.startAt;
		if (!startAt) return true;

		const elapsedMs = Date.now() - new Date(startAt).getTime();
		return elapsedMs < POL_MULTIPLE_FACES_START_COOLDOWN_SEC * 1000;
	}

	private getFaceLandmarksBBox(
		landmarks: NormalizedLandmark[],
		canvasWidth: number,
		canvasHeight: number,
	): { centerX: number; centerY: number; area: number } {
		let minX = 1;
		let minY = 1;
		let maxX = 0;
		let maxY = 0;

		for (const point of landmarks) {
			minX = Math.min(minX, point.x);
			minY = Math.min(minY, point.y);
			maxX = Math.max(maxX, point.x);
			maxY = Math.max(maxY, point.y);
		}

		const width = (maxX - minX) * canvasWidth;
		const height = (maxY - minY) * canvasHeight;

		return {
			centerX: ((minX + maxX) / 2) * canvasWidth,
			centerY: ((minY + maxY) / 2) * canvasHeight,
			area: width * height,
		};
	}

	private capturePolDebugFrameForKo(): string | null {
		if (!this.isCurrentFrameUsable() || !this.ctx || !this.canvas || !this.video) {
			return null;
		}

		try {
			this.syncPolAnalysisCanvasFromVideo();
			this.drawImageForDocumentValidation();
			return this.imageData16x9?.startsWith('data:image/') ? this.imageData16x9 : null;
		} catch {
			return null;
		}
	}

	/*
	 * ImageEmbedder
	 */
	private async photosAreSimilar(photos: Array<string>): Promise<ISimilarity> {
		if (photos.length >= 2) {
			await this.waitLoadingPhotos(photos);
			const environment1 = await this.imageEmbedder.embed(
				document.getElementById('uploadImage1') as HTMLImageElement,
			);
			const environment2 = await this.imageEmbedder.embed(
				document.getElementById('uploadImage2') as HTMLImageElement,
			);
			const regionOfInterest = { left: 0.2, top: 0.15, right: 0.6, bottom: 0.75 };
			const face1 = await this.imageEmbedder.embed(document.getElementById('uploadImage1') as HTMLImageElement, {
				regionOfInterest: regionOfInterest,
			});
			const face2 = await this.imageEmbedder.embed(document.getElementById('uploadImage2') as HTMLImageElement, {
				regionOfInterest: regionOfInterest,
			});
			(document.getElementById('uploadImage1') as HTMLImageElement).src = '';
			(document.getElementById('uploadImage2') as HTMLImageElement).src = '';
			return {
				environment:
					ImageEmbedder.cosineSimilarity(environment1.embeddings[0], environment2.embeddings[0]) * 100,
				face: ImageEmbedder.cosineSimilarity(face1.embeddings[0], face2.embeddings[0]) * 100,
			};
		}
		return { environment: 0, face: 0 };
	}

	private waitLoadingPhotos(photos: Array<string>): Promise<boolean> {
		(document.getElementById('uploadImage1') as HTMLImageElement).src = photos[0];
		(document.getElementById('uploadImage2') as HTMLImageElement).src = photos[1];
		return new Promise((resolve) => {
			timer(250).subscribe(() => {
				resolve(true);
			});
		});
	}

	private async getSimilarityAllLifeTestPhotos(): Promise<{
		similarity?: Array<ISimilarity>;
		result: number;
	}> {
		const similarityLifeTest: Array<ISimilarity> = [];
		let result: number = 0;
		if (this.lifeCheckConf.middleFace.photos.length) {
			for (const lifeTest of this.lifeCheckConf.lifeTest) {
				if (lifeTest.photos.length) {
					/*
					 * Comparamos la primera foto capturada de la persona en la fase 1
					 * con cada una de las primeras fotos de las pruebas de vida
					 */
					similarityLifeTest.push(
						await this.photosAreSimilar([
							this.lifeCheckConf.middleFace.photos[0].img,
							lifeTest.photos[0].img,
						]),
					);
				}
			}
			similarityLifeTest.forEach((similarity: ISimilarity) => {
				result += similarity.environment + similarity.face;
			});
			return {
				similarity: similarityLifeTest,
				result: similarityLifeTest.length && result / (similarityLifeTest.length * 2),
			};
		}
		return { result: 0 };
	}

	/*
	 * Configuración de las emociones
	 */
	private addNosePointToPath(type: LIFE_TEST_ID): void {
		if (
			type !== LIFE_TEST_ID.SMILE &&
			(this.lifeCheckConf.lifeTest.find(
				(lt: ILifeTest) => type === lt.lifeTestId && lt.status !== TestStatus.OnComplete,
			) ||
				type === LIFE_TEST_ID.MIDDLEFACE) &&
			this.match
		) {
			this.nosePath.push(this.positionFaceService.getNosePoint());
			this.ctx.beginPath();
			this.ctx.moveTo(this.nosePath[0].x, this.nosePath[0].y);
			this.nosePath.forEach((point: IPoint) => {
				this.ctx.lineTo(point.x, point.y);
			});
			this.ctx.stroke();
		}
	}

	/*
	 * Gestión de los textos
	 */
	private shouldRefreshPolUiTextsThisFrame(): boolean {
		if (!this.isPolLivenessProcessingFrame()) return true;
		return this.lifeTestCycleFrameCounter % POL_UI_REFRESH_EVERY_N_FRAMES === 0;
	}

	private refreshTemplateCaches(): void {
		this.darkModeEnabled = this.designService.isDarkMode();
		this.qrUrl = this.auth.getQRUrl() || '';
		this.lifeCheckProgressValue = this.lifeCheckConf ? this.progressService.getValue(this.lifeCheckConf) : 0;
	}

	/** Regenera subtítulo/alerta solo si cambian (menos CD y `trans.instant` en rAF). */
	private refreshPolUiTexts(): void {
		this.refreshTemplateCaches();
		this.updateMiddleFaceMinTimeCountdown();
		if (this.isPolAudioHistoryManagingUi()) {
			return;
		}
		const nextSubtitle = this.generateSubtitle();
		if (nextSubtitle !== this.subtitle) this.subtitle = nextSubtitle;
	}

	/** Cuenta atrás visible sobre el óvalo durante el minTime de centrado (solo si sigue encuadrado). */
	private updateMiddleFaceMinTimeCountdown(): void {
		const middleFace = this.lifeCheckConf?.middleFace;
		if (
			!middleFace ||
			middleFace.complete ||
			middleFace.status === TestStatus.OnComplete ||
			this.activeLifeTestMinTimeGate !== LIFE_TEST_ID.MIDDLEFACE ||
			this.middleFaceMinTimeStartedAtMs == null ||
			!this.matchUi
		) {
			if (this.middleFaceMinTimeCountdownSeconds !== null) {
				this.middleFaceMinTimeCountdownSeconds = null;
			}
			return;
		}
		const minTimeMs = middleFace.minTime * POL_MIN_TIME_MS_PER_SECOND;
		if (minTimeMs <= 0) {
			this.middleFaceMinTimeCountdownSeconds = null;
			return;
		}
		const elapsedMs = Date.now() - this.middleFaceMinTimeStartedAtMs;
		const remainingSeconds = Math.ceil((minTimeMs - elapsedMs) / 1000);
		const next = remainingSeconds > 0 ? remainingSeconds : null;
		if (this.middleFaceMinTimeCountdownSeconds !== next) {
			this.middleFaceMinTimeCountdownSeconds = next;
		}
	}

	public updateSubtitle(): void {
		this.subtitle = this.generateSubtitle();
	}

	/** Subtítulo onComplete de la prueba recién cerrada durante la pausa entre gestos. */
	private getInterTestOnCompleteSubtitle(): string {
		if (!this.interTestTransition || !this.completingPolTestId || !this.lifeCheckConf) {
			return '';
		}
		if (this.completingPolTestId === LIFE_TEST_ID.MIDDLEFACE) {
			return '';
		}
		const lifeTest = this.lifeCheckConf.lifeTest.find((lt) => lt.lifeTestId === this.completingPolTestId);
		const messageKey = lifeTest?.messages[TestStatus.OnComplete];
		return messageKey ? this.trans.instant(messageKey) : '';
	}

	/**
	 * Intenta cerrar la fase de centrado cuando ya cumplió el minTime configurado.
	 *
	 * No valida scoring por su cuenta: delega en `finalizePolMinTimePhase`, que aplica las reglas
	 * habituales (`isMiddleFaceValid`, fotos, etc.). Evita marcar timeout o KO cuando el usuario
	 * ya completó la ventana de 3 s pero el cierre no se ejecutó en el mismo frame.
	 *
	 * @returns `true` si `middleFace.complete` quedó en true tras la finalización.
	 */
	private async tryCompleteMiddleFaceFromValidation(): Promise<boolean> {
		const middleFace = this.lifeCheckConf.middleFace;
		if (
			middleFace.complete ||
			middleFace.status === TestStatus.OnComplete ||
			middleFace.status === TestStatus.OnFail
		) {
			return middleFace.complete;
		}
		if (
			this.activeLifeTestMinTimeGate === LIFE_TEST_ID.MIDDLEFACE &&
			this.isPolMinTimeElapsed(LIFE_TEST_ID.MIDDLEFACE) &&
			!this.polMinTimeFinalizeInFlight
		) {
			await this.finalizePolMinTimePhase(LIFE_TEST_ID.MIDDLEFACE);
		}
		return middleFace.complete;
	}

	/*
	 * Controles de posición de la cara en el centro
	 */
	private isMatch(lifeTest: ILifeTest | IMiddleFace): void {
		this.addNosePointToPath(lifeTest.lifeTestId);
		this.positionFaceService.setControlRect(lifeTest.lifeTestId, this.canvas);
		const centered =
			(this.results?.faceBlendshapes?.length ?? 0) > 0 && this.positionFaceService.isMatch(lifeTest.lifeTestId);
		this.updateMatch(centered);
	}

	/**
	 * Actualiza `match` (muestra de scoring) y aplica histéresis a `matchUi` (óvalo en pantalla).
	 * La validación sigue usando `match`; la UI exige rachas para no alternar línea continua/discontinua por jitter.
	 */
	private updateMatch(centered: boolean): void {
		this.match = centered;
		this.applyMatchUiReading(centered);
	}

	private resetMatchUiTracking(): void {
		this.matchUiStreakReadings = [];
		this.matchUi = false;
	}

	private applyMatchUiReading(centered: boolean): void {
		if (this.matchUi === centered) {
			this.matchUiStreakReadings = [];
			return;
		}
		const readings = this.matchUiStreakReadings;
		const last = readings[readings.length - 1];
		if (last !== centered) {
			this.matchUiStreakReadings = [centered];
			return;
		}
		readings.push(centered);
		const requiredStreak = centered ? POL_MATCH_UI_STREAK_TO_CENTERED : POL_MATCH_UI_STREAK_TO_OFF_CENTER;
		if (readings.length < requiredStreak) {
			return;
		}
		this.matchUi = centered;
		this.matchUiStreakReadings = [];
		if (!centered) {
			this.resetMiddleFaceMinTimeIfActive();
		}
	}

	/** Cancela la ventana minTime de centrado para reiniciar cuenta atrás y timer al reencuadrar. */
	private resetMiddleFaceMinTimeIfActive(): void {
		const middleFace = this.lifeCheckConf?.middleFace;
		if (
			!middleFace ||
			middleFace.complete ||
			middleFace.status === TestStatus.OnComplete ||
			this.activeLifeTestMinTimeGate !== LIFE_TEST_ID.MIDDLEFACE ||
			this.middleFaceMinTimeStartedAtMs == null
		) {
			return;
		}
		this.middleFaceMinTimeCountdownSeconds = null;
		// Sin este reset el timer de minTime no se rearma al volver a encuadrar (óvalo verde sin cuenta atrás).
		this.middleFaceMinTimeStartedAtMs = null;
		this.resetTimers();
		this.scoringFaceService.setValidationMatch([]);
		if (middleFace.photos.length > 0) {
			middleFace.photos = [];
		}
	}

	/*
	 * Nav Help
	 */
	public navHelpChange(action: INavHelp): void {
		const actions = {
			[NavHelpAction.ROTATE]: () => this.changeCamera(),
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.HELP]: () => this.displayHelpWarning(true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.VOLUMEN]: () => this.navHelpService.toggleAudioGuide(action),
		};
		return actions[action.id]();
	}

	public changeCamera(): void {
		this.markCameraChangeGrace();
		this.cameraService.changeCamera();
	}

	public onCameraSwitchStarted(): void {
		this.polCameraSwitchInProgress = true;
		onCameraPreviewSwitchStarted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}

	public onCameraSwitchCompleted(): void {
		this.polCameraSwitchInProgress = false;
		onCameraPreviewSwitchCompleted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
		void this.restartPolRecordingAfterCameraSwitchIfNeeded();
	}

	private async restartPolRecordingAfterCameraSwitchIfNeeded(): Promise<void> {
		try {
			if (!this.streamingManagementService.isStreamingActive()) {
				return;
			}
			const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();
			if (!isRecordingRequired) {
				return;
			}
			const stream = this.cameraService.stream ?? this.stream;
			if (stream?.active) {
				await this.streamingManagementService.restartRecordingOnStream(stream);
			}
		} catch {
			// No bloquear la UX si falla el reinicio de grabación tras switch.
		}
	}

	/*
	 * Timer
	 */
	public startTest(): void {
		this.startTotalTime();
	}

	// Total time
	private startTotalTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startTotalTime();
	}

	private stopTotalTime(): void {
		this.timerManagerService.stopTotalTime();
	}

	// Warning time
	private startWarningTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startWarningTime(() => {
			if (this.timerManagerService.warningTime >= POL_WARNING_TIME) {
				this.stopWarningTime();
				this.lifeCheckConf.general.status = TestStatus.OnFail;
				this.sendResult(false, this.getLivenessStatus());
			}
		});
	}

	public stopWarningTime(): void {
		this.timerManagerService.stopWarningTime();
	}

	private startNoFaceTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startNoFaceTime(() => {
			if (!this.isCurrentFrameUsable()) {
				void this.handleUnusableFrame('noFaceTimerFrameNotUsable');
				return;
			}
			if (this.timerManagerService.noFaceTime >= POL_NO_FACE_TIME) {
				if (this.isIntermittentNoFacePattern()) {
					void this.recoverPreview('noFaceIntermittent');
				}
				if (this.lifeCheckConf.general.countTries + 1 > this.lifeCheckConf.general.tries) {
					if (this.availableToLogRecoveryFailure()) {
						this.cloudLogsService.sendCloudLogs(
							{
								noFaceOutcome: {
									mode: 'finalKo',
									noFaceTime: this.timerManagerService.noFaceTime,
									noFaceConsecutiveFrames: this.noFaceConsecutiveFrames,
									msSinceLastFace: this.lastFaceDetectedAtMs
										? Date.now() - this.lastFaceDetectedAtMs
										: null,
									intermittentNoFace: this.isIntermittentNoFacePattern(),
									analysisDeadWarned: this.polAnalysisDeadWarned,
									...this.buildPolAttemptDiagContext(),
								},
							},
							PolLog.IN_PROCESS,
							LogType.Warning,
						);
					}
					this.lifeCheckConf.general.status = TestStatus.OnFail;
					this.sendResult(false, TEST_STATUS.KO_NO_FACE);
					this.stopTotalTime();
					this.stopNoFaceTime();
				} else {
					this.cloudLogsService.sendCloudLogs(
						{
							noFaceOutcome: {
								mode: 'pageWarningRetry',
								noFaceTime: this.timerManagerService.noFaceTime,
								noFaceConsecutiveFrames: this.noFaceConsecutiveFrames,
								msSinceLastFace: this.lastFaceDetectedAtMs
									? Date.now() - this.lastFaceDetectedAtMs
									: null,
								intermittentNoFace: this.isIntermittentNoFacePattern(),
								analysisDeadWarned: this.polAnalysisDeadWarned,
								...this.buildPolAttemptDiagContext(),
							},
						},
						PolLog.IN_PROCESS,
						LogType.Warning,
					);
					this.setPageWarning(PAGE_WARNING.NO_FACE);
					this.stopTotalTime();
					this.stopNoFaceTime();
					this.startWarningTime();
				}
			}
		});
	}

	private stopNoFaceTime(): void {
		this.timerManagerService.stopNoFaceTime();
	}

	private isIntermittentNoFacePattern(): boolean {
		const now = Date.now();
		const msSinceLastFace = this.lastFaceDetectedAtMs ? now - this.lastFaceDetectedAtMs : Number.MAX_SAFE_INTEGER;
		const shortNoFaceStreak = this.noFaceConsecutiveFrames < POL_NO_FACE_MIN_CONSECUTIVE_FRAMES;
		const detectedFaceRecently = msSinceLastFace < POL_NO_FACE_RECENT_FACE_GRACE_MS;
		return shortNoFaceStreak || detectedFaceRecently;
	}

	// KO Help time
	private startKOHelpTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startKOHelpTime(() => {
			if (this.timerManagerService.KOHelpTime >= POL_WARNING_TIME) {
				this.stopKOHelpTime();
				this.sendResult(false, this.getLivenessStatus());
				// this.verificationFail(TEST_STATUS.KO_HELP);
			}
		});
	}

	public stopKOHelpTime(): void {
		this.timerManagerService.stopKOHelpTime();
	}

	private getLivenessStatus(): TEST_STATUS {
		if (this.pageWarning.show) {
			switch (this.pageWarning.type) {
				case PAGE_WARNING.HELP:
					return TEST_STATUS.KO_HELP;
				case PAGE_WARNING.NO_FACE:
					return TEST_STATUS.KO_NO_FACE;
				case PAGE_WARNING.MULTIPLE_FACES:
					return TEST_STATUS.KO_MULTIPLE_FACES;
				case PAGE_WARNING.CAMERA_ERROR:
					return TEST_STATUS.KO_CAMERA;
			}
		}
		if (this.handsetLandscape) {
			return TEST_STATUS.KO_HANDSET_LANDSCAPE;
		}
		if (this.operation.config.tutorial) {
			return TEST_STATUS.KO_TUTORIAL;
		}
		return TEST_STATUS.KO;
	}

	private getTutorialLottiePath(): string {
		return this.darkModeEnabled
			? 'assets/animations/pwcs-prueba-vida-dark.json'
			: 'assets/animations/pwcs-prueba-vida-light.json';
	}

	private refreshTutorialLottieOptions(): void {
		const path = this.getTutorialLottiePath();
		this.tutorialLottieSub?.unsubscribe();
		this.tutorialLottieSub = this.lottieThemeService
			.loadThemedAnimation(path)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (options) => {
					this.tutorialLottieOptions = options ?? { path };
				},
				error: () => {
					this.tutorialLottieOptions = { path };
				},
			});
	}

	/*
	 * Tutorial
	 */
	public async finishTutorial(): Promise<void> {
		this.operation.config.tutorial = false;
		this.stopWarningTime();
		this.beginPolTutorialSubtitleHandoff();
		if (!this.loading.show) {
			this.updateSubtitle();
			this.scheduleEndPolTutorialSubtitleHandoff();
		}
		this.audioService.preloadLifeCheckAudios();

		// Verificar streaming antes de permitir continuar (modo estricto)
		try {
			const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

			if (isRecordingRequired) {
				try {
					// Esperar a que el streaming esté listo antes de continuar
					await this.streamingManagementService.waitForStreamingReady();
				} catch (streamingError: any) {
					// MODO ESTRICTO: Si required_recording está activo, el streaming es OBLIGATORIO
					this.cloudLogsService.sendCloudLogs(
						{ pol: `Streaming connection failed after tutorial - Aborting: ${streamingError?.toString()}` },
						PolLog.IN_PROCESS,
						LogType.Error,
					);

					if (!this.streamingManagementService.isStreamingRetryInProgress()) {
						// Mostrar pantalla de error con botón de reintento
						this.errorService.setStreamingError(async () => {
							await this.streamingManagementService.retryStreaming(() => this.errorService.clearError());
						});
					}
					return;
				}
			}
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ pol: `Error checking streaming in finishTutorial: ${error?.toString()}` },
				PolLog.IN_PROCESS,
				LogType.Error,
			);
		}

		if (!this.operation.config.iframe) {
			this.fullscreenService.enterFullscreen();
		}
		if (this.iframeSwitchCamera) {
			this.changeCamera();
		}
		if (!this.loading.show) {
			this.updateSubtitle();
		}
	}

	private beginPolTutorialSubtitleHandoff(): void {
		this.polTutorialSubtitleHandoff = true;
	}

	private endPolTutorialSubtitleHandoff(): void {
		this.polTutorialSubtitleHandoff = false;
		if (this.polTutorialSubtitleHandoffTimeoutId != null) {
			clearTimeout(this.polTutorialSubtitleHandoffTimeoutId);
			this.polTutorialSubtitleHandoffTimeoutId = null;
		}
	}

	/** Cierra el handoff del header tras montar el copy en el óvalo (sin loading activo). */
	private scheduleEndPolTutorialSubtitleHandoff(): void {
		if (this.polTutorialSubtitleHandoffTimeoutId != null) {
			clearTimeout(this.polTutorialSubtitleHandoffTimeoutId);
		}
		this.polTutorialSubtitleHandoffTimeoutId = setTimeout(() => {
			if (!this.loading.show) {
				this.endPolTutorialSubtitleHandoff();
			}
			this.polTutorialSubtitleHandoffTimeoutId = null;
		}, 100);
	}

	/** Regenera subtítulo/alerta al terminar el spinner de preview (p. ej. «Iniciando cámara»). */
	private refreshPolUiAfterCameraPreviewReady(): void {
		this.updateSubtitle();
		this.endPolTutorialSubtitleHandoff();
	}

	/*
	 * Iframe
	 */
	public sendDataIframe(status: IFRAME_RESULT, observations?: string): void {
		if (!this.sendDataComplete) {
			this.sendDataComplete = true;
			if (!environment.production) {
				console.log(`PostMessage: ${status}`);
			}
			window.top.postMessage({ code: 'life-test', status: status, observations: observations ?? undefined }, '*');
		}
	}

	private resetTimers(): void {
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
			this.subscriptionTimers = new Subscription();
		}
		this.activeLifeTestMinTimeGate = null;
		this.polMinTimeFinalizeInFlight = false;
	}

	/*
	 * Controles
	 */
	private setControls(lifeTest: any): void {
		this.controls = {
			...this.controls,
			lifeTestId: lifeTest.lifeTestId,
			minPercentageFaceBlendshape: lifeTest.minPercentageFaceBlendshape,
			minPercentageTransition: lifeTest.minTransitionsFaceBlendshape,
			similarityPercentage: this.scoringFaceService.getSimilarity(),
		};
		if (lifeTest.noseMovement) {
			this.controls = {
				...this.controls,
				noseMovement: {
					...this.controls.noseMovement,
					minPercentageVariance: lifeTest.noseMovement?.minPercentageVariance,
					minDistance: lifeTest.noseMovement?.minDistance,
					minTransitions: lifeTest.noseMovement?.minTransitions,
				},
			};
		} else {
			this.controls.noseMovement = undefined;
		}
	}

	private resetControls(): void {
		this.controls = {
			...this.controls,
			percentageTimeInCamera: 0,
			percentageLifeTest: 0,
			percentageTransition: 0,
			noseMovement: {
				...this.controls.noseMovement,
				percentageVariance: 0,
				distance: 0,
				transitions: 0,
			},
		};
	}

	public lifeTestCompleted(): number {
		return this.lifeCheckConf?.lifeTest?.filter((lt: ILifeTest) => lt.complete)?.length || 0;
	}

	private setPolSetting(setting: IPolSetting): IPolConf {
		if (setting?.available_tests && setting?.available_tests.length) {
			lifeCheckConf.lifeTest = lifeCheckConf.lifeTest.filter((lt: ILifeTest) =>
				setting.available_tests.includes(lt.lifeTestId),
			);
		}
		const requestedTotal = setting?.total_test ?? lifeCheckConf.general.totalLifeTest;
		const maxByAvailable = Math.max(1, lifeCheckConf.lifeTest.length);
		return {
			...lifeCheckConf,
			general: {
				...lifeCheckConf.general,
				tries: setting?.total_tries ?? lifeCheckConf.general.tries,
				type: setting?.type ?? lifeCheckConf.general.type,
				totalLifeTest: Math.min(requestedTotal, maxByAvailable),
				minScoring: +setting?.scoring || lifeCheckConf.general.minScoring,
			},
		};
	}

	private displayHelpWarning(isHelpByNavHelp: boolean, observations: string | undefined = undefined): void {
		this.isHelpByNavHelp = isHelpByNavHelp;
		this.setPageWarning(PAGE_WARNING.HELP);
		this.stopTotalTime();
		this.stopNoFaceTime();
		// Si es help por segundo intento (!isHelpByNavHelp),
		// iniciar timer para "No hemos podido verificar tu identidad"
		if (!isHelpByNavHelp) {
			this.startKOHelpTime();
		}
	}

	/**
	 * Establece el tipo y la visibilidad de la advertencia en la página.
	 *
	 * Esta función actualiza el objeto `pageWarning` para mostrar u ocultar una advertencia en la interfaz,
	 * y define el tipo de advertencia que se debe mostrar.
	 *
	 * @param type El tipo de advertencia de la página, puede ser `PAGE_WARNING` o `null`.
	 * @param show Indica si se debe mostrar la advertencia en la página. Por defecto es `true`.
	 */
	private setPageWarning(type: PAGE_WARNING | null, show: boolean = true): void {
		this.pageWarning.show = show;
		this.pageWarning.type = type;

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	/**
	 * Activa el indicador de carga mostrando un mensaje.
	 *
	 * @param textKey Clave opcional para el texto de carga a mostrar. Si no se proporciona, se utiliza 'GENERAL.LOADING.LIFE_CHECK_VALIDATION' por defecto.
	 */
	private activateLoading(textKey?: string): void {
		this.loading = {
			show: true,
			text: this.trans.instant(textKey ?? 'GENERAL.LOADING.LIFE_CHECK_VALIDATION'),
		};
	}

	// #region HISTORY
	private isPolAudioHistoryManagingUi(): boolean {
		if (!this.canRunTestTimers()) return false;
		if (this.operation?.config?.tutorial) return false;
		if (this.loading.show || this.pageWarning.show || this.handsetLandscape) return false;
		if (this.lifeCheckConf?.general?.complete) return false;
		if (this.completingPolTestId) return true;
		if (this.interTestTransition) return false;

		const { general, middleFace } = this.lifeCheckConf;
		if (general.status === TestStatus.OnFail || general.status === TestStatus.OnWarning) return true;

		if (!middleFace.complete && middleFace.status === TestStatus.OnInit) {
			return true;
		}

		const lifeTest = this.getCurrentPendingLifeTest();
		if (!lifeTest) return false;

		if (lifeTest.status === TestStatus.OnInit) {
			return true;
		}

		if (
			lifeTest.status === TestStatus.OnWarning &&
			lifeTest.messages[TestStatus.OnWarning] !== general.messages[TestStatus.OnWarningMismatch]
		) {
			return true;
		}

		return false;
	}

	private startSecondlyDataCapture(): void {
		if (!this.canRunTestTimers()) return;
		if (this.secondlyTimerSubscription) {
			this.secondlyTimerSubscription.unsubscribe();
		}

		this.secondlyTimerSubscription = interval(POL_SEG_HISTORY_INTERVAL * 1000)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.capturePolDataSnapshot();
			});
	}

	private stopSecondlyDataCapture(): void {
		if (this.secondlyTimerSubscription) {
			this.secondlyTimerSubscription.unsubscribe();
			this.secondlyTimerSubscription = null;
		}
	}

	private getActivePolTestId(): LIFE_TEST_ID | null {
		if (!this.lifeCheckConf) return null;
		if (this.completingPolTestId) {
			return this.completingPolTestId;
		}
		if (!this.lifeCheckConf.middleFace.complete) {
			return LIFE_TEST_ID.MIDDLEFACE;
		}
		return this.getCurrentPendingLifeTest()?.lifeTestId ?? null;
	}

	private getPolSnapshotComplete(): boolean {
		if (!this.lifeCheckConf) return false;

		const activeTestId = this.getActivePolTestId();
		if (!activeTestId) return false;

		if (activeTestId === LIFE_TEST_ID.MIDDLEFACE) {
			const { middleFace } = this.lifeCheckConf;
			return middleFace.status === TestStatus.OnComplete && middleFace.complete;
		}

		const lifeTest = this.lifeCheckConf.lifeTest.find((test) => test.lifeTestId === activeTestId);
		return lifeTest?.status === TestStatus.OnComplete && lifeTest?.complete === true;
	}

	private capturePolLifeTestValidationFlags(): Pick<
		IPolHistory,
		'hasInsufficientTransitions' | 'hasInvalidHeadMovements' | 'hasInsufficientEmotion'
	> {
		const activeTestId = this.getActivePolTestId();
		if (!activeTestId || activeTestId === LIFE_TEST_ID.MIDDLEFACE) {
			return {
				hasInsufficientTransitions: false,
				hasInvalidHeadMovements: false,
				hasInsufficientEmotion: false,
			};
		}

		const lifeTest = this.getCurrentPendingLifeTest();
		if (!lifeTest || lifeTest.lifeTestId !== activeTestId) {
			return {
				hasInsufficientTransitions: false,
				hasInvalidHeadMovements: false,
				hasInsufficientEmotion: false,
			};
		}

		const transitions = this.scoringFaceService.getLifeTestTransition(this.scoringFaceService.getLifeTestEmotion());
		const emotionPct = this.scoringFaceService.getPercentageLifeTestEmotion(
			this.scoringFaceService.getValidationEmotion(),
		);

		return {
			hasInsufficientTransitions: transitions < lifeTest.minTransitionsFaceBlendshape,
			hasInvalidHeadMovements: !this.scoringFaceService.areHeadMovementsValid(lifeTest, this.nosePath),
			hasInsufficientEmotion: emotionPct <= lifeTest.minPercentageFaceBlendshape,
		};
	}

	private capturePolDataSnapshot(): void {
		if (this.operation?.completed) return;
		if (this.lifeCheckConf?.general?.complete || this.lifeCheckConf?.general?.status === TestStatus.OnComplete) {
			return;
		}
		if (this.pageWarning?.show || this.loading?.show) return;
		if (!this.isPolAudioHistoryManagingUi()) return;

		const validationFlags = this.capturePolLifeTestValidationFlags();
		const activeTestId = this.getActivePolTestId();
		if (activeTestId && this.canvas?.width) {
			this.positionFaceService.setControlRect(activeTestId, this.canvas);
		}

		const hasFace = (this.results?.faceBlendshapes?.length ?? 0) > 0;
		const isTooFar = this.positionFaceService.isPolTooFar(hasFace, this.match);
		const isTooClose = this.positionFaceService.isPolTooClose(hasFace);

		const snapshot: IPolHistory = {
			timestamp: new Date().toISOString(),
			faceDetection: hasFace,
			isTooFar,
			isTooClose,
			isOffCenter: this.positionFaceService.isPolOffCenter(hasFace, this.match),
			activeTestId: this.getActivePolTestId(),
			hasInsufficientTransitions: validationFlags.hasInsufficientTransitions,
			hasInvalidHeadMovements: validationFlags.hasInvalidHeadMovements,
			hasInsufficientEmotion: validationFlags.hasInsufficientEmotion,
			complete: this.getPolSnapshotComplete(),
			speech: null,
			speechStateId: null,
		};

		this.polAudioHistoryService.addDataHistory(snapshot);
		this.textAndSpeechByHistory();
	}

	private textAndSpeechByHistory(): void {
		if (this.operation?.completed) return;
		if (this.lifeCheckConf?.general?.complete || this.lifeCheckConf?.general?.status === TestStatus.OnComplete) {
			return;
		}
		if (!this.polAudioHistoryService.dataHistoryArray.length) return;
		if (!this.isPolAudioHistoryManagingUi()) return;

		const lastSeconds: IPolHistory[] = this.polAudioHistoryService.getDataHistoryLastSecs(1);
		const activeTestId = this.getActivePolTestId();

		if (this.polAudioHistoryService.dataHistoryArray.length === 1) {
			const message: string = this.polAudioHistoryService.getInitTestMsg(this.lifeCheckConf, activeTestId);
			if (message) {
				this.tts.speak(message);
				this.polAudioHistoryService.dataHistoryArray.at(0).speech = message;
				this.subtitle = this.trans.instant(message);
			}
			return;
		}

		const speechIndex = findLastIndex(
			this.polAudioHistoryService.dataHistoryArray,
			(history: IPolHistory) => history.speech,
		);
		let lastSpeech: { speech: string; index: number; stateId: string | null } = {
			speech: '',
			index: 0,
			stateId: null,
		};
		if (speechIndex !== -1) {
			lastSpeech = {
				speech: this.polAudioHistoryService.dataHistoryArray[speechIndex]?.speech,
				index: speechIndex,
				stateId: this.polAudioHistoryService.dataHistoryArray[speechIndex]?.speechStateId ?? null,
			};
		}

		let hasCondition = false;
		const states = this.polAudioHistoryService.getStatesForActiveTest(this.lifeCheckConf, activeTestId);
		for (let index = 0; index < states.length; index++) {
			const state = states[index];
			if (state.conditions()) {
				const actualConf = state.promptConfig.find((conf) => conf.audio === lastSpeech.speech);
				if (actualConf && lastSpeech.stateId === state.id) {
					this.setAlertScanFaceText(actualConf);
					this.setPolAlertContent(actualConf);
				}
				if (
					this.polAudioHistoryService.shouldAdvancePrompt(
						state,
						lastSpeech.index,
						lastSpeech.speech,
						lastSpeech.stateId,
						this.polAudioHistoryService.dataHistoryArray.length,
					)
				) {
					if (state.promptConfig?.length) {
						if (state.promptConfig.length === 1) {
							this.tts.speak(state.promptConfig[0].audio, true);
							lastSeconds.at(0).speech = state.promptConfig[0].audio;
							lastSeconds.at(0).speechStateId = state.id;
							this.setAlertScanFaceText(state.promptConfig[0]);
							this.setPolAlertContent(state.promptConfig[0]);
						}
						if (state.promptConfig.length > 1) {
							const promptIndex = this.polAudioHistoryService.resolvePromptIndex(
								state,
								lastSpeech.speech,
								lastSpeech.stateId,
							);
							if (promptIndex === -1) {
								this.tts.speak(state.promptConfig[0].audio);
								lastSeconds.at(0).speech = state.promptConfig[0].audio;
								lastSeconds.at(0).speechStateId = state.id;
								this.setAlertScanFaceText(state.promptConfig[0]);
								this.setPolAlertContent(state.promptConfig[0]);
							} else {
								const nextIndex = (promptIndex + 1) % state.promptConfig.length;
								this.tts.speak(state.promptConfig[nextIndex].audio);
								lastSeconds.at(0).speech = state.promptConfig[nextIndex].audio;
								lastSeconds.at(0).speechStateId = state.id;
								this.setAlertScanFaceText(state.promptConfig[nextIndex]);
								this.setPolAlertContent(state.promptConfig[nextIndex]);
							}
						}
					}
				}
				hasCondition = true;
				break;
			}
		}
		if (!hasCondition) {
			this.alertScanFaceText = '';
		}
	}

	private setAlertScanFaceText(conf: IPolPromptConfig): void {
		this.alertScanFaceText = conf.centerText;
	}

	private setPolAlertContent(conf: IPolPromptConfig): void {
		this.alertContent = conf?.keepPrevAlert ? this.alertContent : (conf.alert as IAlert);
	}
	// #endregion HISTORY

	// #region Gestión de los textos
	/**
	 * Genera el subtítulo adecuado para la interfaz de verificación de vida según el estado actual de la aplicación.
	 *
	 * Evalúa múltiples condiciones relacionadas con permisos de cámara, orientación del dispositivo, advertencias de página,
	 * estado de carga, y progreso de las pruebas de vida. Devuelve el texto traducido correspondiente para mostrar como subtítulo,
	 * o una cadena vacía si no corresponde mostrar ningún mensaje.
	 *
	 * Además, si el mensaje cambia, reproduce el audio asociado al nuevo texto.
	 *
	 * @returns {string} El subtítulo traducido para mostrar en la interfaz, o una cadena vacía si no aplica.
	 */
	private generateSubtitle(): string {
		if (
			!this.permissionsCamera ||
			this.handsetLandscape ||
			this.pageWarning.type === PAGE_WARNING.VERIFICATION_FAIL ||
			this.pageWarning.type === PAGE_WARNING.SERVER_ERROR ||
			this.pageWarning.type === PAGE_WARNING.CAMERA_ERROR ||
			this.pageWarning.type === PAGE_WARNING.BACK_TO_OCR
		) {
			return '';
		}

		if (this.pageWarning.type === PAGE_WARNING.HELP) {
			return this.trans.instant('LIFE_CHECK.HELP.DESCRIPTION');
		}

		if (this.operation?.config.tutorial) {
			return this.trans.instant('LIFE_CHECK.TUTORIAL_TITLE');
		}

		if (this.loading.show || this.pageWarning.show || this.handsetLandscape) {
			if (this.loading.show && this.polTutorialSubtitleHandoff && this.subtitle) {
				return this.subtitle;
			}
			return '';
		}

		const interTestOnComplete = this.getInterTestOnCompleteSubtitle();
		if (interTestOnComplete) {
			return interTestOnComplete;
		}

		if (
			!this.operation?.config.tutorial &&
			this.lifeCheckConf &&
			this.lifeCheckConf?.general.status !== TestStatus.OnComplete
		) {
			if (!this.lifeCheckConf?.middleFace.complete) {
				let message = '';
				message = this.lifeCheckConf.middleFace.messages[TestStatus.OnInit];
				const translatedMessage = this.trans.instant(message);
				// Audio
				return translatedMessage;
			}

			if (this.lifeCheckConf?.lifeTest.length) {
				const found = this.getCurrentPendingLifeTest();
				if (found) {
					let message = found.messages[TestStatus.OnInit];

					if (found.status === TestStatus.OnWarning) {
						message = found.messages[TestStatus.OnWarning];
					}

					if (found.status === TestStatus.OnComplete) {
						message = found.messages[TestStatus.OnComplete];
					}

					const translatedMessage = this.trans.instant(message);

					// Audio
					return translatedMessage;
				}

				return '';
			}
			return this.trans.instant(this.lifeCheckConf?.general.messages[TestStatus.OnInit] ?? '');
		}
		return '';
	}

	// #region Life Test Cycle
	/**
	 * Inicializa la prueba de vida si aún no ha comenzado.
	 */
	private initializeTestIfNeeded(): void {
		if (!this.canRunTestTimers()) return;
		if (this.lifeCheckConf.general && !this.lifeCheckConf.general.startAt) {
			this.startSecondlyDataCapture();
			this.startTest();
			this.lifeCheckConf.general.startAt = new Date().toISOString();
			this.capturePolDataSnapshot();
		}
	}

	/**
	 * Maneja el fallo general de la prueba de vida.
	 */
	private handleGeneralFailure(): boolean {
		if (this.lifeCheckConf.general.status === TestStatus.OnFail) {
			if (!this.lifeCheckConf.general.finishAt) {
				this.controls = { ...this.controls, lifeTestId: 'Final' };
				this.lifeCheckConf.general.finishAt = new Date().toISOString();
			}
			return true;
		}

		return false;
	}

	// #region Encuadrar la cara
	/**
	 * Determina si se debe procesar la detección de la cara en la posición central.
	 */
	private shouldProcessMiddleFace(): boolean {
		const middleFace = this.lifeCheckConf.middleFace;
		return !middleFace.complete && middleFace.status !== TestStatus.OnComplete;
	}

	/**
	 * Procesa la fase de reconocimiento facial de la parte media del rostro.
	 */
	private async processMiddleFacePhase(): Promise<void> {
		// Igual que en `collectMiddleFaceResults`: no actualizar `match` con geometría obsoleta.
		if (this.polDetectRanThisFrame) {
			this.isMatch(this.lifeCheckConf.middleFace);
		}
		if (this.lifeCheckConf.middleFace.status === TestStatus.OnInit) {
			this.resetAnimations();
		}
		this.done = this.isMiddleFaceOvalAligned();
		this.setControls(this.lifeCheckConf.middleFace);
		await this.middleFaceValidation();
		// Tras acumular muestras, cerrar centrado si el minTime ya expiró (p. ej. último frame encuadrado).
		await this.tryCompleteMiddleFaceFromValidation();
	}

	/** Óvalo verde en centrado; en gestos usa histéresis `matchUi` como antes. */
	public getPolScanFaceMatch(): boolean {
		if (!this.lifeCheckConf.middleFace.complete) {
			return this.isMiddleFaceOvalAligned();
		}
		return this.matchUi;
	}

	/** Óvalo verde solo si la UI suavizada y la validación instantánea coinciden. */
	public isMiddleFaceOvalAligned(): boolean {
		return this.matchUi && this.match;
	}

	/**
	 * Restablece las animaciones de la detección facial a su estado inicial.
	 */
	private resetAnimations(): void {
		this.animationsService.setAnimations(this.scanFaceAnimation, {
			grow: false,
			check: false,
			halfRight: false,
			halfLeft: false,
			halfTop: false,
			halfBottom: false,
			smile: false,
			showArrow: false,
			positionArrow: null,
			progressValue: 0,
		});
	}
	// #endregion

	// #region Validaciones para el centrado de la cara
	/**
	 * Procesa la advertencia de la fase intermedia (middleFace) del chequeo de vida.
	 *
	 * Si el tiempo máximo de la fase ha expirado, marca la fase como fallida y delega en
	 * `generalTries` (solo intentos generales) para reiniciar todo el proceso o cerrar en KO.
	 */
	private processMiddleFaceWarning(): void {
		if (!this.lifeCheckConf.middleFace.startAt) return;
		const startDate = new Date(this.lifeCheckConf.middleFace.startAt);
		startDate.setSeconds(startDate.getSeconds() + this.lifeCheckConf.middleFace.maxTime);
		void this.processMiddleFaceWarningAsync(startDate);
	}

	/** Antes de KO por maxTime: intentar cerrar centrado si el minTime ya terminó con gates OK. */
	private async processMiddleFaceWarningAsync(maxTimeDeadline: Date): Promise<void> {
		if (await this.tryCompleteMiddleFaceFromValidation()) return;
		// Revisamos si ha pasado el tiempo de la FASE 1 para pasar a estado OnFail
		if (maxTimeDeadline < new Date()) {
			await this.handleMiddleFaceMaxTimeExpired();
		}
	}

	/** Última oportunidad de cerrar centrado antes de KO por timeout (evita falso timeout con gates OK). */
	private async handleMiddleFaceMaxTimeExpired(): Promise<void> {
		if (await this.tryCompleteMiddleFaceFromValidation()) {
			return;
		}
		await this.finalizePolMinTimePhase(LIFE_TEST_ID.MIDDLEFACE);
		const middleFace = this.lifeCheckConf.middleFace;
		if (middleFace.complete || middleFace.status === TestStatus.OnComplete) {
			return;
		}
		middleFace.status = TestStatus.OnFail;
		this.generalTries(`Tiempo máximo agotado (centrado): ${middleFace.lifeTestId}`);
	}

	/**
	 * Procesa la inicialización de la fase intermedia de verificación facial.
	 *
	 * Calcula si ha transcurrido la mitad del tiempo máximo permitido para la fase intermedia
	 * y, en ese caso, cambia el estado a `OnWarning`. Además, gestiona el temporizador que controla
	 * si el rostro del usuario está fuera del área de control externo, iniciando o deteniendo el temporizador
	 * según corresponda.
	 */
	private processMiddleFaceInit(): void {
		if (!this.lifeCheckConf.middleFace.startAt) return;
		const startDate = new Date(this.lifeCheckConf.middleFace.startAt);
		startDate.setSeconds(startDate.getSeconds() + this.lifeCheckConf.middleFace.maxTime / 2);
		// Revisamos si ha pasado la mitad del tiempo de la FASE 1 para pasar a estado OnWarning
		if (startDate < new Date()) {
			this.lifeCheckConf.middleFace.status = TestStatus.OnWarning;
		}
	}

	/** Pinta en el canvas de análisis el mismo recorte que ve el usuario (cover + zoom opcional). */
	private syncPolAnalysisCanvasFromVideo(extraZoomFactor: number = this.getEffectivePolPreviewZoomFactor()): void {
		if (!this.video || !this.canvas || !this.ctx) return;
		const preview = this.getPreviewRect(this.video);
		drawVideoCoverFrameToCanvas(
			this.video,
			this.canvas,
			this.ctx,
			preview?.width ?? this.video.videoWidth,
			preview?.height ?? this.video.videoHeight,
			extraZoomFactor,
		);
	}

	/**
	 * Crea una imagen recortada para el payload `/liveness`.
	 * Zoom-out de payload + canvas completo en PC y móvil (NIF-951).
	 */
	private drawImageForDocumentValidation(): void {
		if (!this.ctx || !this.video) return;

		this.syncPolAnalysisCanvasFromVideo(this.polPayloadZoomFactor);

		if (this.polPayloadUseFullCanvas) {
			this.imageData16x9 = this.canvas.toDataURL('image/jpeg', quality);
			return;
		}

		const { srcX, srcY, srcWidth, srcHeight } = getPolLivenessJpegCrop(this.canvas.width, this.canvas.height);
		if (srcWidth <= 0 || srcHeight <= 0) return;

		const tempCanvas = document.createElement('canvas');
		tempCanvas.width = srcWidth;
		tempCanvas.height = srcHeight;
		const tempCtx = tempCanvas.getContext('2d');

		if (!tempCtx) return;

		tempCtx.drawImage(this.canvas, srcX, srcY, srcWidth, srcHeight, 0, 0, tempCanvas.width, tempCanvas.height);

		this.imageData16x9 = tempCanvas.toDataURL('image/jpeg', quality);
		return;
	}

	/**
	 * Recoge y valida los resultados de la fase intermedia del test de vida facial.
	 *
	 * Este método gestiona la lógica para almacenar imágenes capturadas durante la fase intermedia,
	 * valida la coincidencia facial, controla los temporizadores necesarios y actualiza el estado
	 * del test según los resultados obtenidos. Si la validación es exitosa, marca la fase como completa,
	 * registra logs en la nube y realiza las animaciones correspondientes. En caso contrario, reinicia
	 * la colección de imágenes para intentar nuevamente.
	 *
	 * - Almacena la primera y última imagen de la fase intermedia.
	 * - Utiliza temporizadores para asegurar el tiempo mínimo de validación.
	 * - Actualiza el estado y los resultados del test según la validación facial.
	 * - Envía logs a la nube y gestiona animaciones y puntos de referencia faciales.
	 */
	private async collectMiddleFaceResults(): Promise<void> {
		const middleFace = this.lifeCheckConf.middleFace;
		if (middleFace.complete || middleFace.status === TestStatus.OnComplete) {
			return;
		}
		if (this.scoringFaceService.getValidationMatch().length === 0) {
			this.scoringFaceService.addLifeTestMatch(this.match);
			if (this.match) {
				this.scoringFaceService.addValidationMatch(this.match);
				// Fase 1: Almacenamos primera imagen
				if (middleFace.photos.length === 0) {
					this.drawImageForDocumentValidation();
					middleFace.photos.push({
						img: this.imageData16x9,
						label: this.cameraService.getCurrentDeviceLabel(),
					});
				}
				this.markPolMinTimeStarted(LIFE_TEST_ID.MIDDLEFACE);
			}
		} else {
			this.scoringFaceService.addValidationMatch(this.match);
			this.scoringFaceService.addLifeTestMatch(this.match);
			// Tras el primer match, la rama `if` ya arrancó minTime; aquí hay que hacerlo en reencuadres.
			if (this.match) {
				this.markPolMinTimeStarted(LIFE_TEST_ID.MIDDLEFACE);
			}
			if (this.isPolMinTimeElapsed(LIFE_TEST_ID.MIDDLEFACE)) {
				await this.finalizePolMinTimePhase(LIFE_TEST_ID.MIDDLEFACE);
			}
		}
	}
	// #endregion

	// #region Pruebas de vida
	/**
	 * Determina si existe alguna prueba de vida pendiente.
	 */
	private shouldProcessLifeTests(): boolean {
		return this.isThereAnyPendingLifeTest();
	}

	/** Zoom preview+análisis: base UA × boost de fase lejos approach-face. */
	public getEffectivePolPreviewZoomFactor(): number {
		return this.polPreviewZoomFactor * this.approachFaceZoomBoost;
	}

	private resetApproachFaceSession(): void {
		this.approachFacePhase = 'idle';
		this.approachFaceFarRoi = null;
		this.approachFaceNearRoi = null;
		this.approachFaceLiveRoi = null;
		this.approachFaceHoldAnchorMs = 0;
		this.approachFaceCenteredAccumMs = 0;
		this.approachFacePhaseStartedAtMs = 0;
		this.approachFaceReadyAnchorMs = 0;
		this.approachFaceMatchSamples = [];
		this.approachFaceZoomBoost = 1;
		this.approachFaceHoldCountdownSeconds = null;
		this.scanFaceAnimation.progressValue = 0;
		this.animationsService.setAnimations(this.scanFaceAnimation, { grow: false });
	}

	/**
	 * NIF-739: protocolo lejos → acercarse → cerca con scoring de FaceDepthRealLabService.
	 */
	private async runApproachFaceLifeTest(lifeTest: ILifeTest): Promise<void> {
		this.initializeLifeTestStartTime(lifeTest);
		if (lifeTest.status === TestStatus.OnFail || lifeTest.status === TestStatus.OnComplete || lifeTest.complete) {
			return;
		}
		// maxTime desde startAt: en smile/turns el reloj solo corre en OnWarning porque
		// processLifeTestInit pasa a OnWarning a mitad de maxTime. En approach-face OnWarning
		// es la fase de acercamiento, no el reloj — hay que evaluar timeout también en OnInit (far).
		if (lifeTest.status === TestStatus.OnInit || lifeTest.status === TestStatus.OnWarning) {
			this.processLifeTestWarning(lifeTest);
		}
		const statusAfterWarning = lifeTest.status as TestStatus;
		if (statusAfterWarning === TestStatus.OnFail || statusAfterWarning === TestStatus.OnComplete) {
			this.resetApproachFaceSession();
			return;
		}
		if (lifeTest.status === TestStatus.OnInit) {
			this.processLifeTestInit(lifeTest);
		}

		if (this.approachFacePhase === 'idle' || this.approachFacePhase === 'done') {
			this.approachFacePhase = 'far';
			this.approachFaceZoomBoost = approachFaceProtocol.farZoom;
			this.approachFaceMatchSamples = [];
			this.approachFaceFarRoi = null;
			this.approachFaceNearRoi = null;
			this.approachFaceHoldAnchorMs = 0;
			this.approachFaceCenteredAccumMs = 0;
			this.approachFaceReadyAnchorMs = 0;
			this.approachFacePhaseStartedAtMs = performance.now();
			if (lifeTest.status !== TestStatus.OnInit) {
				lifeTest.status = TestStatus.OnInit;
			}
			// Como middle-face: óvalo sólido/punteado por `match`; sin grow permanente.
			this.animationsService.setAnimations(this.scanFaceAnimation, {
				grow: false,
				check: false,
				progressValue: 0,
			});
			this.updateSubtitle();
		}

		const landmarks = this.results?.faceLandmarks?.[0];
		const now = performance.now();
		if (landmarks?.length && this.canvas && this.ctx) {
			const texture = this.sampleApproachFaceTexture(landmarks);
			const estimated = this.faceDepthRealLab.buildRoiSnapshotFromLandmarks(
				landmarks,
				now,
				this.getEffectivePolPreviewZoomFactor(),
				texture,
			);
			if (estimated) {
				this.approachFaceLiveRoi = estimated;
			}
		} else {
			this.approachFaceLiveRoi = null;
		}

		this.approachFaceMatchSamples.push(this.match);

		if (this.approachFacePhase === 'far') {
			this.tickApproachFaceFar(now, lifeTest);
		} else if (this.approachFacePhase === 'approach_cue') {
			this.tickApproachFaceCue(now);
		} else if (this.approachFacePhase === 'near') {
			this.tickApproachFaceNear(now, lifeTest);
		}
	}

	private tickApproachFaceFar(now: number, lifeTest: ILifeTest): void {
		const holdOk = this.match && !!this.approachFaceLiveRoi && this.approachFaceLiveRoi.validSampleRatio > 0.2;
		if (holdOk) {
			if (!this.approachFaceHoldAnchorMs) {
				this.approachFaceHoldAnchorMs = now;
			}
			this.approachFaceCenteredAccumMs = now - this.approachFaceHoldAnchorMs;
		} else {
			this.approachFaceHoldAnchorMs = 0;
			this.approachFaceCenteredAccumMs = 0;
			this.approachFaceFarRoi = null;
		}
		this.scanFaceAnimation.progressValue = Math.min(
			100,
			(this.approachFaceCenteredAccumMs / approachFaceProtocol.farHoldMs) * 100,
		);
		this.updateApproachFaceHoldCountdown(approachFaceProtocol.farHoldMs, holdOk);
		if (
			!this.approachFaceFarRoi &&
			holdOk &&
			this.approachFaceCenteredAccumMs >= approachFaceProtocol.farHoldMs * 0.5
		) {
			this.approachFaceFarRoi = this.cloneApproachFaceRoi(this.approachFaceLiveRoi!);
		}
		if (holdOk && this.approachFaceCenteredAccumMs >= approachFaceProtocol.farHoldMs && this.approachFaceLiveRoi) {
			if (!this.approachFaceFarRoi) {
				this.approachFaceFarRoi = this.cloneApproachFaceRoi(this.approachFaceLiveRoi);
			}
			this.approachFacePhase = 'approach_cue';
			this.approachFacePhaseStartedAtMs = now;
			this.approachFaceCenteredAccumMs = 0;
			this.approachFaceHoldAnchorMs = 0;
			this.approachFaceReadyAnchorMs = 0;
			this.approachFaceZoomBoost = 1;
			this.approachFaceHoldCountdownSeconds = null;
			this.scanFaceAnimation.progressValue = 0;
			this.animationsService.setAnimations(this.scanFaceAnimation, { grow: false });
			this.promoteApproachFaceToApproachPhase(lifeTest);
		}
	}

	/** Far → cue/near: frase de acercamiento (`IS_TOO_FAR_1`) vía OnWarning. */
	private promoteApproachFaceToApproachPhase(lifeTest: ILifeTest): void {
		lifeTest.status = TestStatus.OnWarning;
		const messageKey = lifeTest.messages[TestStatus.OnWarning];
		if (messageKey) {
			this.tts.speak(messageKey, true);
			this.subtitle = this.trans.instant(messageKey);
		}
		this.updateSubtitle();
	}

	private tickApproachFaceCue(now: number): void {
		this.approachFaceHoldCountdownSeconds = null;
		if (!this.approachFaceFarRoi || !this.approachFaceLiveRoi) {
			return;
		}
		const scaleRatio = this.approachFaceFarRoi.meanDepthM / Math.max(0.05, this.approachFaceLiveRoi.meanDepthM);
		const depthDrop = this.approachFaceFarRoi.meanDepthM - this.approachFaceLiveRoi.meanDepthM;
		const closeEnough =
			scaleRatio >= approachFaceProtocol.minScaleRatio &&
			depthDrop >= DEFAULT_FACE_DEPTH_REAL_THRESHOLDS.minDeltaDepthM;
		const phaseElapsedOk = now - this.approachFacePhaseStartedAtMs >= approachFaceProtocol.minPhaseMs;
		this.scanFaceAnimation.progressValue = Math.min(
			99,
			(scaleRatio / Math.max(0.001, approachFaceProtocol.minScaleRatio)) * 100,
		);
		if (!this.match || !closeEnough || !phaseElapsedOk) {
			this.approachFaceReadyAnchorMs = 0;
			return;
		}
		if (!this.approachFaceReadyAnchorMs) {
			this.approachFaceReadyAnchorMs = now;
		}
		const readyHold = now - this.approachFaceReadyAnchorMs;
		this.scanFaceAnimation.progressValue = Math.min(100, (readyHold / approachFaceProtocol.readyHoldMs) * 100);
		if (readyHold >= approachFaceProtocol.readyHoldMs) {
			this.approachFacePhase = 'near';
			this.approachFaceCenteredAccumMs = 0;
			this.approachFaceHoldAnchorMs = 0;
			this.approachFaceReadyAnchorMs = 0;
			this.approachFacePhaseStartedAtMs = now;
			this.scanFaceAnimation.progressValue = 0;
		}
	}

	private tickApproachFaceNear(now: number, lifeTest: ILifeTest): void {
		if (!this.approachFaceFarRoi || !this.approachFaceLiveRoi) {
			this.approachFaceHoldCountdownSeconds = null;
			return;
		}
		const scaleRatio = this.approachFaceFarRoi.meanDepthM / Math.max(0.05, this.approachFaceLiveRoi.meanDepthM);
		const closeEnough = scaleRatio >= approachFaceProtocol.minScaleRatio * 0.98;
		const holdOk = this.match && closeEnough && this.approachFaceLiveRoi.validSampleRatio > 0.2;
		if (holdOk) {
			if (!this.approachFaceHoldAnchorMs) {
				this.approachFaceHoldAnchorMs = now;
			}
			this.approachFaceCenteredAccumMs = now - this.approachFaceHoldAnchorMs;
		} else {
			this.approachFaceHoldAnchorMs = 0;
			this.approachFaceCenteredAccumMs = 0;
		}
		this.scanFaceAnimation.progressValue = Math.min(
			100,
			(this.approachFaceCenteredAccumMs / approachFaceProtocol.nearHoldMs) * 100,
		);
		this.updateApproachFaceHoldCountdown(approachFaceProtocol.nearHoldMs, holdOk);
		if (!holdOk || this.approachFaceCenteredAccumMs < approachFaceProtocol.nearHoldMs) {
			return;
		}
		this.approachFaceHoldCountdownSeconds = null;
		this.approachFaceNearRoi = this.cloneApproachFaceRoi(this.approachFaceLiveRoi);
		const scores = this.faceDepthRealLab.scoreApproach(
			this.approachFaceFarRoi,
			this.approachFaceNearRoi,
			getApproachFaceLabThresholds(this.polPreviewDevice),
		);
		this.approachFacePhase = 'done';
		this.finishApproachFaceAttempt(lifeTest, scores);
	}

	/**
	 * Misma lógica visual que `updateMiddleFaceMinTimeCountdown`: segundos restantes
	 * mientras el hold far/near acumula tiempo con rostro centrado.
	 */
	private updateApproachFaceHoldCountdown(holdMs: number, holdActive: boolean): void {
		if (!holdActive || holdMs <= 0) {
			if (this.approachFaceHoldCountdownSeconds !== null) {
				this.approachFaceHoldCountdownSeconds = null;
			}
			return;
		}
		const remainingSeconds = Math.ceil((holdMs - this.approachFaceCenteredAccumMs) / 1000);
		const next = remainingSeconds > 0 ? remainingSeconds : null;
		if (this.approachFaceHoldCountdownSeconds !== next) {
			this.approachFaceHoldCountdownSeconds = next;
		}
	}

	private getApproachFaceCenteredPercentage(): number {
		const samples = this.approachFaceMatchSamples;
		if (!samples.length) {
			return 0;
		}
		const centered = samples.filter(Boolean).length;
		return (centered * 100) / samples.length;
	}

	private finishApproachFaceAttempt(lifeTest: ILifeTest, scores: FaceDepthRealScores): void {
		this.drawImageForDocumentValidation();
		lifeTest.photos = [
			{
				img: this.imageData16x9,
				label: this.cameraService.getCurrentDeviceLabel(),
			},
		];
		const labThresholds = getApproachFaceLabThresholds(this.polPreviewDevice);

		if (scores.passed) {
			lifeTest.status = TestStatus.OnComplete;
			this.resetAnimations();
			this.animationsService.checkAnimationByTest(this.scanFaceAnimation, lifeTest);
			this.resetTimers();
			if (!lifeTest.finishAt) {
				lifeTest.finishAt = new Date().toISOString();
			}
			this.scoringFaceService.calcApproachFaceScoring(
				lifeTest,
				scores,
				this.getApproachFaceCenteredPercentage(),
				true,
				labThresholds,
			);
			const hasNextLifeTest = !this.isLastLifeTest();
			this.completeLifeTest(lifeTest);
			if (hasNextLifeTest) {
				this.startInterTestTransition();
				this.updateSubtitle();
			}
			return;
		}

		lifeTest.photos = [];
		if (!lifeTest.finishAt) {
			lifeTest.finishAt = new Date().toISOString();
		}
		this.scoringFaceService.calcApproachFaceScoring(
			lifeTest,
			scores,
			this.getApproachFaceCenteredPercentage(),
			true,
			labThresholds,
		);
		this.resetApproachFaceSession();
		lifeTest.status = TestStatus.OnFail;
		this.generalTries(
			`Approach-face no superado: final=${scores.finalScore} shape=${scores.shapeScore} appearance=${scores.appearanceScore} deltaM=${scores.deltaDepthM.toFixed(3)} gates=${scores.gateDeltaOk}/${scores.gateShapeOk}/${scores.gateAppearanceOk}/${scores.gateFlatShapeOk}`,
		);
	}

	private cloneApproachFaceRoi(roi: FaceDepthRoiSnapshot): FaceDepthRoiSnapshot {
		return {
			...roi,
			keyOffsets: roi.keyOffsets?.map((o) => ({ dx: o.dx, dy: o.dy })),
			texture: roi.texture
				? {
						laplacianVar: roi.texture.laplacianVar,
						patch16: new Float32Array(roi.texture.patch16),
					}
				: null,
		};
	}

	private sampleApproachFaceTexture(landmarks: NormalizedLandmark[]) {
		if (!this.ctx || !this.canvas) {
			return null;
		}
		let minX = 1;
		let maxX = 0;
		let minY = 1;
		let maxY = 0;
		for (const p of landmarks) {
			if (!p) {
				continue;
			}
			minX = Math.min(minX, p.x);
			maxX = Math.max(maxX, p.x);
			minY = Math.min(minY, p.y);
			maxY = Math.max(maxY, p.y);
		}
		const padX = (maxX - minX) * 0.08;
		const padY = (maxY - minY) * 0.08;
		return extractFaceRoiTextureMetrics(this.ctx, {
			x0: Math.max(0, minX - padX),
			y0: Math.max(0, minY - padY),
			x1: Math.min(1, maxX + padX),
			y1: Math.min(1, maxY + padY),
		});
	}

	/**
	 * Procesa la fase actual de la prueba de vida.
	 */
	private async processLifeTestPhase(): Promise<void> {
		if (this.handleInterTestTransition()) {
			return;
		}
		// Revisamos que prueba no está completada por orden y seteamos el recuadro de control
		const lifeTest = this.getCurrentPendingLifeTest();
		if (lifeTest) {
			if (lifeTest.lifeTestId === LIFE_TEST_ID.APPROACHFACE) {
				this.prepareScanUiForLifeTest(lifeTest);
				this.isMatch(lifeTest);
				// Igual que centrado: `done` verde cuando matchUi+match; punteado vía `!match`.
				this.done = this.matchUi && this.match;
				await this.runApproachFaceLifeTest(lifeTest);
				return;
			}
			this.prepareScanUiForLifeTest(lifeTest);
			this.done = false;
			this.animationsService.animationArrowByTest(
				lifeTest,
				this.scoringFaceService.getFaceBlendshapes(lifeTest.lifeTestId, this.results?.faceBlendshapes ?? []),
				this.scanFaceAnimation,
			);
			this.isMatch(lifeTest);
			// Parar la animación si el rostro no está centrado
			if (!this.match) {
				this.animationsService.setAnimations(this.scanFaceAnimation, { showArrow: false });
			}
			this.setControls(lifeTest);
			// FASE 2: Validaciones
			await this.lifeTestValidation(lifeTest);
		}
	}
	// #endregion

	// #region Validaciones para las pruebas de vida
	/**
	 * Inicializa la propiedad `startAt` de un objeto `ILifeTest` con la fecha y hora actual en formato ISO si aún no ha sido definida.
	 *
	 * @param lifeTest Objeto que representa la prueba de vida actual a procesar.
	 */
	private initializeLifeTestStartTime(lifeTest: ILifeTest): void {
		if (!lifeTest.startAt) {
			lifeTest.startAt = new Date().toISOString();
		}
	}

	/**
	 * Procesa el estado de advertencia de una prueba de vida (life test).
	 *
	 * Si el tiempo máximo de la prueba ha expirado, marca el test como fallido y delega en
	 * `generalTries` (solo intentos generales). También gestiona descentrado y mensajes de aviso.
	 *
	 * @param lifeTest Objeto que representa la prueba de vida actual a procesar.
	 */
	private processLifeTestWarning(lifeTest: ILifeTest): void {
		const startDate = new Date(lifeTest.startAt);
		startDate.setSeconds(startDate.getSeconds() + lifeTest.maxTime);
		// Revisamos si ha pasado el tiempo de la prueba para pasar a estado OnFail
		if (startDate < new Date()) {
			lifeTest.status = TestStatus.OnFail;
			const approachPhaseSuffix =
				lifeTest.lifeTestId === LIFE_TEST_ID.APPROACHFACE ? ` phase=${this.approachFacePhase}` : '';
			this.generalTries(`Tiempo máximo agotado: ${lifeTest.lifeTestId}${approachPhaseSuffix}`);
		}
	}

	/**
	 * Inicializa el proceso de verificación de vida (life test) y actualiza el estado del test si ha transcurrido
	 * más de la mitad del tiempo máximo permitido. Si el usuario ha pasado menos del 25% del tiempo en el centro
	 * y aún no hay coincidencia, se asigna un mensaje de advertencia específico.
	 *
	 * @param lifeTest Objeto que contiene la información y estado actual de la prueba de vida.
	 */
	private processLifeTestInit(lifeTest: ILifeTest): void {
		// approach-face: OnWarning lo dispara el cambio de fase (far → cue), no el reloj.
		if (lifeTest.lifeTestId === LIFE_TEST_ID.APPROACHFACE) {
			return;
		}
		const startDate = new Date(lifeTest.startAt);
		startDate.setSeconds(startDate.getSeconds() + lifeTest.maxTime / 2);
		// Revisamos si ha pasado la mitad del tiempo de la prueba para pasar a estado OnWarning
		if (startDate < new Date()) {
			lifeTest.status = TestStatus.OnWarning;
		}
	}

	/**
	 * Recoge y procesa los resultados de una prueba de vida (life test) utilizando los blendshapes faciales detectados.
	 * Dependiendo del estado de la prueba y de la coincidencia facial (`match`), gestiona la animación, almacena imágenes
	 * capturadas, valida emociones y coincidencias, y controla el flujo de finalización de la prueba.
	 *
	 * Si la cara está correctamente centrada y se cumplen las condiciones, almacena imágenes del usuario, valida la prueba
	 * tras un tiempo mínimo requerido y actualiza el estado de la prueba. También gestiona los temporizadores y el envío de logs.
	 *
	 * @param lifeTest Objeto que representa la prueba de vida actual, incluyendo su estado, identificador y fotos asociadas.
	 */
	// #region minTime compartido (centrado + gestos, NIF-385)
	private getPolMinTimeSeconds(testId: LIFE_TEST_ID): number {
		if (testId === LIFE_TEST_ID.MIDDLEFACE) {
			return this.lifeCheckConf.middleFace.minTime;
		}
		return this.lifeCheckConf.lifeTest.find((lifeTest) => lifeTest.lifeTestId === testId)?.minTime ?? 0;
	}

	private getMinTimeStartedAtMs(testId: LIFE_TEST_ID): number | null {
		return testId === LIFE_TEST_ID.MIDDLEFACE
			? this.middleFaceMinTimeStartedAtMs
			: this.lifeTestValidationStartedAtMs;
	}

	private setMinTimeStartedAtMs(testId: LIFE_TEST_ID, value: number | null): void {
		if (testId === LIFE_TEST_ID.MIDDLEFACE) {
			this.middleFaceMinTimeStartedAtMs = value;
		} else {
			this.lifeTestValidationStartedAtMs = value;
		}
	}

	private isPolMinTimeElapsed(testId: LIFE_TEST_ID): boolean {
		const startedAtMs = this.getMinTimeStartedAtMs(testId);
		if (startedAtMs == null) {
			return false;
		}
		return Date.now() - startedAtMs >= this.getPolMinTimeSeconds(testId) * POL_MIN_TIME_MS_PER_SECOND;
	}

	private markPolMinTimeStarted(testId: LIFE_TEST_ID): void {
		if (this.getMinTimeStartedAtMs(testId) == null) {
			this.setMinTimeStartedAtMs(testId, Date.now());
		}
		if (this.activeLifeTestMinTimeGate != null) {
			return;
		}
		this.activeLifeTestMinTimeGate = testId;
		this.subscriptionTimers.add(
			timer(this.getPolMinTimeSeconds(testId) * POL_MIN_TIME_MS_PER_SECOND).subscribe(async () => {
				await this.finalizePolMinTimePhase(testId);
			}),
		);
	}

	private canRunPolMinTimeFinalization(testId: LIFE_TEST_ID): boolean {
		if (this.polMinTimeFinalizeInFlight) {
			return false;
		}
		if (this.activeLifeTestMinTimeGate !== testId) {
			return false;
		}
		if (!this.scoringFaceService.getValidationMatch().length) {
			return false;
		}
		if (this.pageWarning.show || this.handsetLandscape) {
			return false;
		}
		if (testId === LIFE_TEST_ID.MIDDLEFACE) {
			const middleFace = this.lifeCheckConf.middleFace;
			return (
				!middleFace.complete &&
				middleFace.status !== TestStatus.OnComplete &&
				middleFace.status !== TestStatus.OnFail
			);
		}
		const lifeTest = this.lifeCheckConf.lifeTest.find((candidate) => candidate.lifeTestId === testId);
		if (!lifeTest) {
			return false;
		}
		return (
			lifeTest.status !== TestStatus.OnComplete &&
			lifeTest.status !== TestStatus.OnFail &&
			this.lifeCheckConf.general.status !== TestStatus.OnWarning
		);
	}

	private async finalizePolMinTimePhase(testId: LIFE_TEST_ID): Promise<void> {
		if (!this.canRunPolMinTimeFinalization(testId) || !this.isPolMinTimeElapsed(testId)) {
			return;
		}
		this.polMinTimeFinalizeInFlight = true;
		this.activeLifeTestMinTimeGate = null;
		try {
			if (testId === LIFE_TEST_ID.MIDDLEFACE) {
				await this.runMiddleFaceMinTimeFinalization();
			} else {
				const lifeTest = this.lifeCheckConf.lifeTest.find((candidate) => candidate.lifeTestId === testId);
				if (lifeTest) {
					await this.runLifeTestMinTimeFinalization(lifeTest);
				}
			}
		} finally {
			this.polMinTimeFinalizeInFlight = false;
		}
	}

	private async runMiddleFaceMinTimeFinalization(): Promise<void> {
		const middleFace = this.lifeCheckConf.middleFace;
		if (middleFace.photos.length === 1) {
			this.drawImageForDocumentValidation();
			middleFace.photos.push({
				img: this.imageData16x9,
				label: this.cameraService.getCurrentDeviceLabel(),
			});
		}
		if (middleFace.photos.length >= 2) {
			this.scoringFaceService.setSimilarity(
				await this.photosAreSimilar(
					middleFace.photos.map((photo: { img: string; label: string }) => photo.img),
				),
			);
		}
		if (this.isMiddleFaceValid()) {
			this.applyMiddleFaceSuccess();
		} else {
			middleFace.photos = [];
			this.middleFaceMinTimeCountdownSeconds = null;
			this.middleFaceMinTimeStartedAtMs = null;
		}
		this.scoringFaceService.setValidationMatch([]);
	}

	private applyMiddleFaceSuccess(): void {
		const middleFace = this.lifeCheckConf.middleFace;
		if (middleFace.complete || middleFace.status === TestStatus.OnComplete) {
			return;
		}
		middleFace.status = TestStatus.OnComplete;
		middleFace.complete = true;
		this.completingPolTestId = LIFE_TEST_ID.MIDDLEFACE;
		this.done = false;
		this.middleFaceMinTimeCountdownSeconds = null;
		this.middleFaceMinTimeStartedAtMs = null;
		this.resetAnimations();
		this.animationsService.checkAnimationByTest(this.scanFaceAnimation, middleFace);
		this.resetTimers();
		this.positionFaceService.setNoseCenterPoint(this.nosePath);
		if (!middleFace.finishAt) {
			middleFace.finishAt = new Date().toISOString();
		}
		this.scoringFaceService.calcMiddleFaceScoring(middleFace, true);
		this.nosePath = [];
		this.scoringFaceService.setValidationMatch([]);
	}

	private async runLifeTestMinTimeFinalization(lifeTest: ILifeTest): Promise<void> {
		if (lifeTest.photos.length === 1) {
			this.drawImageForDocumentValidation();
			lifeTest.photos.push({
				img: this.imageData16x9,
				label: this.cameraService.getCurrentDeviceLabel(),
			});
		}
		if (
			this.lifeCheckConf.middleFace?.photos?.length &&
			this.lifeCheckConf.middleFace.photos[0]?.img &&
			lifeTest.photos?.length &&
			lifeTest.photos[0]?.img
		) {
			this.scoringFaceService.setSimilarity(
				await this.photosAreSimilar([this.lifeCheckConf.middleFace.photos[0].img, lifeTest.photos[0].img]),
			);
		}
		if (this.isLifeTestValid(lifeTest)) {
			lifeTest.status = TestStatus.OnComplete;
			this.resetAnimations();
			this.animationsService.checkAnimationByTest(this.scanFaceAnimation, lifeTest);
			this.resetTimers();
			if (!lifeTest.finishAt) {
				lifeTest.finishAt = new Date().toISOString();
			}
			this.scoringFaceService.calcLifeTestScoring(lifeTest, true);
			// Marcar complete al instante para que la siguiente prueba (p. ej. turn-right tras smile) arranque
			// sin seguir procesando la anterior durante timeBetweenTest.
			const hasNextLifeTest = !this.isLastLifeTest();
			this.completeLifeTest(lifeTest);
			if (hasNextLifeTest) {
				this.startInterTestTransition();
				this.updateSubtitle();
			}
		} else {
			lifeTest.photos = [];
			this.lifeTestValidationStartedAtMs = null;
		}
		this.scoringFaceService.setValidationMatch([]);
		this.scoringFaceService.setValidationEmotion([]);
	}

	private async collectLifeTestResults(lifeTest: ILifeTest): Promise<void> {
		if (lifeTest.complete || lifeTest.status === TestStatus.OnComplete) {
			return;
		}
		const faceBlendshapes = this.scoringFaceService.getFaceBlendshapes(
			lifeTest.lifeTestId,
			this.results?.faceBlendshapes ?? [],
		);
		if (this.match) {
			this.done = false;
			if (lifeTest.status === TestStatus.OnInit || lifeTest.status === TestStatus.OnWarning) {
				this.animationsService.growAnimationByTest(
					this.scanFaceAnimation,
					lifeTest,
					faceBlendshapes,
					this.lifeCheckConf.general,
					this.lifeTestValidationStartedAtMs,
				);
			}
		}
		if (this.scoringFaceService.getValidationEmotion().length === 0) {
			// FASE 2: Si la cara está en el centro empezamos a guardar resultados
			this.scoringFaceService.addLifeTestMatch(this.match);
			if (this.match) {
				this.scoringFaceService.addValidationMatch(this.match);
				// Giros L/R: solo acumular emoción si nariz y gesto pueden ir a la vez (NIF-644).
				if (this.scoringFaceService.isInstantTurnNoseDistanceMet(lifeTest)) {
					this.scoringFaceService.addValidationEmotion(faceBlendshapes);
					this.scoringFaceService.addLifeTestEmotion(faceBlendshapes);
					this.markPolMinTimeStarted(lifeTest.lifeTestId);
				}
				// Fase 2: Prueba almacenamos primera imagen
				if (lifeTest.photos.length === 0) {
					this.drawImageForDocumentValidation();
					lifeTest.photos.push({
						img: this.imageData16x9,
						label: this.cameraService.getCurrentDeviceLabel(),
					});
				}
			}
		} else {
			this.scoringFaceService.addValidationMatch(this.match);
			this.scoringFaceService.addLifeTestMatch(this.match);
			if (this.scoringFaceService.isInstantTurnNoseDistanceMet(lifeTest)) {
				this.scoringFaceService.addValidationEmotion(faceBlendshapes);
				this.scoringFaceService.addLifeTestEmotion(faceBlendshapes);
			}
			if (this.isPolMinTimeElapsed(lifeTest.lifeTestId)) {
				await this.finalizePolMinTimePhase(lifeTest.lifeTestId);
			}
		}
	}
	// #endregion

	// #region Últimas validaciones
	/**
	 * Determina si se debe procesar la validación final.
	 */
	private shouldProcessFinalValidation(): boolean {
		return !this.lifeCheckConf.general.finishAt && !this.lifeCheckConf.general.controlValidation;
	}

	/**
	 * Finaliza el proceso de validación de vida del usuario.
	 */
	private async finishValidation(): Promise<void> {
		this.activateLoading();
		this.lifeCheckConf.general.controlValidation = true;
		const { valid, breakdown } = await this.computePolFinalValidation();
		if (valid) {
			this.controls = { ...this.controls, lifeTestId: 'Final' };
			this.resetAnimations();
			this.lifeCheckConf.general.status = TestStatus.OnComplete;
			this.lifeCheckConf.general.complete = true;
			this.sendResult(true);
		} else {
			this.generalTries(`Problemas con: general`);
		}
	}
	// #endregion

	ngOnDestroy(): void {
		if (this.polTutorialSubtitleHandoffTimeoutId != null) {
			clearTimeout(this.polTutorialSubtitleHandoffTimeoutId);
			this.polTutorialSubtitleHandoffTimeoutId = null;
		}
		this.positionFaceService.setDraw(false);
		this.tutorialLottieSub?.unsubscribe();
		// Verificar si el streaming está activo para decidir si detener los tracks
		const isStreamingActive = this.streamingManagementService?.isStreamingActive() ?? false;

		// Desuscribirse de todos los timers
		if (this.video) {
			this.video.removeEventListener('loadeddata', this.predictWebcam);
		}
		this.video = null;
		// Detener TODOS los timers del servicio
		this.stopTotalTime();
		this.stopNoFaceTime();
		this.stopWarningTime();
		this.stopKOHelpTime();
		this.stopSecondlyDataCapture();
		// Alternativamente, podrías usar: this.timerManagerService.stopAllTimers();
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
		}

		// Solo detener los tracks si NO hay streaming activo
		if (!isStreamingActive && this.stream) {
			this.stream.getTracks().forEach((track) => track.stop());
		}

		this.stream = null;
		this.screenShotService.stopSaveScreenshot();
		this.polPreviewDiagnosticsBuffer.endSession();
		this.cameraPermissionsTimerService.stopWait();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
